/**
 * DecisionFlow — ImportDataPanel
 * ui/src/components/admin/ImportDataPanel.jsx
 * S554 — Import Data / Addin parity
 * S558 D1 — Column mapping step added to EntityUploadPanel
 *
 * Route: /admin/import  (SETUP › Import Data in CompAdminShell)
 *
 * Tab structure (mirrors addin workbench):
 *   Positions | OTE History | RT Shells | Rate Table Rows | Ramp Schedules |
 *   Plans | Plan Assignments | Quotas | Credits | AOP Inputs | Import Log
 *
 * Upload flow per entity (four-step post S558):
 *   1. Choose file  — file picker, filename display
 *   2. Map columns  — fetch saved mapping, confirm/correct source→target per column
 *   3. Validate     — canonical header check against FIELD_CONTRACTS
 *   4. Import       — POST /api/v1/import/upload, result display
 *
 * Column mapping (S558 D1):
 *   GET  /api/v1/import/column-mapping?source_system=direct_api&file_type=X
 *        → [{ column_mapping_id, source_column_name, target_column_name, is_required, is_active }]
 *   POST /api/v1/import/column-mapping
 *        → upsert mapping set for (source_system, file_type)
 *   ensureColumnMapping() retired — mapping is persisted before upload; no silent
 *   POST on import. Mapping is applied client-side (source col renamed to target col)
 *   before the CSV is forwarded to the server.
 *
 * Two-actor approval flows:
 *   RT Shells:
 *     Pending:  GET  /api/v1/addin/lookups/pending_rate_table_approvals
 *     Approve:  POST /api/v1/rate-tables/{rate_table_id}/approve  (per shell)
 *     Error:    SELF_APPROVAL_PROHIBITED
 *
 *   Plans:
 *     Pending:  GET  /api/v1/addin/lookups/pending_plan_approvals
 *     Approve:  POST /api/v1/plans/import/approve  (by plan_import_session_id)
 *     Errors:   SELF_APPROVAL_PROHIBITED | PLAN_ALREADY_APPROVED | PLAN_SESSION_NOT_FOUND
 *     After upload: plan_import_session_id displayed — Actor 1 shares with Actor 2
 *
 * OTE History — per-employee atomicity (S555 D2): 200 | 207 partial | 422
 * All others  — 200 | 207 partial | 422 all-rejected
 *
 * S1002 additions (OI-IMPORT-FEED-REGISTRY, OI-IMPORT-ANALYSE-THEN-DECIDE):
 *   Quota and credit tabs only — all other tabs unchanged.
 *   - REPLACE_MODE_FILE_TYPES, loadMode, replacementScopeKey removed (AD-S1001-IMPORT-ANALYSE-THEN-DECIDE).
 *     Append/Replace toggle replaced by Feed + Analyse-then-Decide gate.
 *   - FeedSelector component added — loads feeds from GET /api/v1/import/feeds.
 *   - NewFeedInline component added — POST /api/v1/import/feeds.
 *   - EntityUploadPanel gains feed state + analyse gate for quota/credit:
 *       STEP.ANALYSING → POST /api/v1/import/analyse
 *       STEP.CONFIRMING → AbsentRowsConfirmation (Keep / Retire / Decide Later)
 *       STEP.FINALISING → POST /api/v1/import/finalise
 *       STEP.PENDING → quarantine result variant
 *   - AbsentRowsConfirmation component added.
 *   - ImportResultCard gains PENDING variant + Retired counter for quota/credit.
 *   - ImportHistoryCard endpoint unchanged (keeps /api/v1/import-logs) — no regression.
 *   Spec: UnifiedImportSpec_v1_5.md §5, DecisionFlow_Import_UX_Spec_v1.md §5.
 *
 * Future OIs:
 *   OI-WEBAPP-AGENT-IMPORT         — guided mode (Guide me + stub wired)
 *   OI-SETUP-EXECUTE-CONSOLIDATION — collapse Setup/Execute at ~80% parity
 *   OI-ADDIN-RT-PLAN-TAB-GROUPING  — align addin to webapp sub-tab grouping
 *
 * Controls (non-negotiable):
 *   CSS: rgb(var(--color-*)) only
 *   Tables: max-h-* + overflow-y-auto + sticky thead
 *   Error envelope: json?.error?.code / json?.detail?.error?.code
 *   No P4-xx codes on any user-facing surface
 */

import { useCallback, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, XCircle, Info,
  Sparkles, Clock, RotateCcw, FileText, Lock, Globe,
} from "lucide-react";
import ImportLogViewer from "../activity/ImportLogViewer.jsx";
import FXRateManager from "../tenant-admin/FXRateManager.jsx"; // C13 S955 — remounted in FX Rates tab
import { useFeatures } from "../../auth/FeatureContext.jsx";  // C13 S955 — feature gate fx_rates tab

// ── Constants ────────────────────────────────────────────────────────────────

const SOURCE_SYSTEM = "direct_api";

// Entity types that use the S1001 Feed + Analyse-then-Decide architecture.
// All other entity types use the original EntityUploadPanel flow unchanged.
// AD-S1001-IMPORT-ANALYSE-THEN-DECIDE, AD-S1001-FEED-REGISTRY (LOCKED S1001).
const FEED_ENTITY_TYPES = new Set(["quota", "credit"]);

const TABS = [
  { id: "positions",      label: "Positions"       },
  { id: "ote_history",    label: "OTE History"     },
  { id: "rate_tables",    label: "RT Shells",      subTabs: [
    { id: "rate_table_shell", label: "RT Shells"        },
    { id: "rate_table_row",   label: "Rate Table Rows"  },
  ]},
  { id: "ramp_schedules", label: "Ramp Schedules" },
  { id: "plans_group",    label: "Plans",           subTabs: [
    { id: "plan",            label: "Plans"             },
    { id: "plan_assignment", label: "Plan Assignments" },
  ]},
  { id: "quotas",         label: "Quotas"          },
  { id: "credits",        label: "Credits"         },
  { id: "mbo_result",     label: "MBO Results"     },                          // S914 G-02
  { id: "nps_result",     label: "NPS Results"     },                          // S930 G-17 — NPS result upload wiring
  { id: "adoption_result",label: "Adoption Results"},                          // S930 G-18 — Adoption result upload wiring
  { id: "churn_events",   label: "Churn Events",   oi: true },                 // G-13 — OI-S910-TIER2-IMPORT-UI-SURFACE-MISSING
  { id: "spiff_awards",   label: "SPIFF Awards",   oi: true },                 // G-12 — OI-S910-TIER2-IMPORT-UI-SURFACE-MISSING
  { id: "myd_schedule",   label: "MYD Schedule",   oi: true },                 // G-15 — OI-S910-TIER2-IMPORT-UI-SURFACE-MISSING
  { id: "aop_inputs",     label: "AOP Inputs" },
  { id: "fx_rates",       label: "FX Rates",         fx: true  },              // C13 S955 — feature-gated; CSV import via /exchange-rates/import
  { id: "import_log",     label: "Import Log",       log: true },
];

// Field contracts — sourced from import_service.py _REQUIRED_TARGETS
const FIELD_CONTRACTS = {
  position: {
    required: ["employee_id", "full_name", "role_code", "segment_code", "hire_date", "is_active"],
    optional: ["ramp_start_date", "manager_employee_id", "territory_code", "calculation_mode"],
    notes: [
      "is_active: true / 1 / yes = active — anything else = inactive",
      "hire_date: ISO format YYYY-MM-DD",
      "employee_id is the identity anchor — used for dedup and FK resolution",
    ],
  },
  ote_history: {
    required: ["employee_id", "ote_amount", "ote_currency_code", "effective_date", "end_date", "ote_period_type"],
    optional: [],
    notes: [
      "All-or-nothing batch — no partial accept. Fix all errors before re-submitting.",
      "ote_amount: decimal string — never a float (e.g. \"125000.00\")",
      "effective_date / end_date: ISO format YYYY-MM-DD",
      "Overlapping date ranges for the same employee are rejected (OTE_OVERLAP_CONFLICT)",
      "end_date is required by router even though nullable in schema",
      "ote_period_type: annual | quarterly | monthly — declares the cadence of ote_amount. annual = full-year figure (Workday / SAP HCM / ADP standard). Engine divides by fiscal_year.period_count to normalize to the calculation period.",
    ],
  },
  rate_table_shell: {
    required: ["rate_table_code", "rate_table_name", "plan_code", "role_code", "segment_code", "default_ceiling_pct", "attainment_ceiling_pct", "effective_date"],
    optional: [],
    notes: [
      "Shells are created unapproved — use the Approve tab after import",
      "default_ceiling_pct: DDL CHECK >= 0.85 AND <= 1.0",
      "attainment_ceiling_pct: DDL CHECK > 1.0",
      "effective_date: ISO format YYYY-MM-DD",
    ],
  },
  rate_table_row: {
    required: ["rate_table_code", "from_pct", "to_pct", "payout_pct", "interpolation"],
    optional: [],
    notes: [
      "Rate table shell must be approved before rows are accepted",
      "interpolation: linear | step",
      "to_pct must be greater than from_pct per row",
      "band_sequence and is_accelerator are server-derived — do not include",
    ],
  },
  plan: {
    required: [
      "plan_code", "plan_name", "role_code", "segment_code", "fiscal_period_code",
      "ote_min", "ote_midpoint", "ote_max", "ote_currency_code",
      "base_pct", "variable_pct",
      "arr_weight", "mbo_weight", "nps_weight", "adoption_weight",
      "effective_date",
    ],
    optional: ["rollup_mode", "business_unit_code"],
    notes: [
      "Plans imported as draft — a second comp admin must approve before assignments can be loaded",
      "rollup_mode: leave blank for IC / AE / CSM / SE roles (server sets NULL)",
      "base_pct + variable_pct must sum to 1.0",
      "arr_weight + mbo_weight + nps_weight + adoption_weight must sum to 1.0",
      "effective_date: ISO format YYYY-MM-DD — controls which fiscal period the plan applies to",
      "business_unit_code: optional — leave blank for NULL-BU global plan, supply for BU-scoped plan",
    ],
  },
  quota: {
    required: ["fiscal_period_code", "quota_local", "quota_local_currency_code"],
    // S979 OI-CREDIT-PF-PL-SENTINEL-PARITY: selling_motion, product_family_code,
    // product_line_code added as optional. Quota grain dimensions per
    // engine_spec_v4_4_final.md §5.6 Amendment 6 (sentinel-default dimensional model).
    // Absent → server canonicalises to sentinel (0 / '__ALL__') — aggregate quota behaviour.
    // Supply real values when tenant requires dimensional quota attribution
    // (e.g. BU-split, product-family-split quota rows per position).
    // Must match credit dimension values for engine wildcard JOIN to resolve correctly.
    optional: ["employee_id", "person_uuid", "business_unit_code", "selling_motion", "product_family_code", "product_line_code"],
    notes: [
      "C-IDENTITY: either employee_id OR person_uuid must be present per row",
      "quota_local: decimal string — never a float",
      "quota_reporting is server-derived — do not include",
      "selling_motion: optional — supply motion_code (e.g. NEW_LOGO, EXPANSION) for selling-motion-dimensional quota. Absent = sentinel (matches all motions).",
      "product_family_code: optional — supply for product-family-dimensional quota. Absent = sentinel (matches all product families). Must match an active product_family record for this tenant.",
      "product_line_code: optional — supply for product-line-dimensional quota. Absent = sentinel '__ALL__' (matches all product lines). Free-form varchar — no FK resolution.",
      "WARNING: mixing sentinel and real values on the same dimension axis for the same position triggers QUOTA_GRAIN_CONFLICT — position excluded from calculation.",
    ],
  },
  credit: {
    required: [
      "employee_id", "source_reference_id", "selling_motion", "business_unit_code",
      "fiscal_period_code", "credit_local", "credit_local_currency_code",
    ],
    // S979 OI-CREDIT-PF-PL-SENTINEL-PARITY: product_family_code and product_line_code
    // added as optional. Credit grain dimensions per engine_spec_v4_4_final.md §5.6
    // Amendment 6 + Amendment 12 (CREDIT-DIMENSIONAL-PARITY-RULE).
    // Absent → server canonicalises to sentinel (0 / '__ALL__') — credit matches any
    // sentinel quota row regardless of product family or product line.
    // Supply real values when tenant quota is dimensional on pf/pl axis — credit
    // must carry matching real dimension for engine JOIN to attribute attainment.
    // Mismatch (real-pf quota + sentinel credit) → CREDIT_DIMENSION_REQUIRED, position excluded.
    optional: ["revenue_type", "deal_close_date", "product_family_code", "product_line_code"],
    notes: [
      "source_reference_id is used for dedup integrity — must be unique per credit event",
      "selling_motion: supply the motion_code (e.g. NEW_LOGO, EXPANSION)",
      "revenue_type: optional — canonical code (e.g. new_logo, expansion, upsell, renewal). Required by engine when Revenue Type Differentials is enabled on the plan. Must be mapped in revenue_type_source_mapping for this tenant before import.",
      "deal_close_date: optional on webapp surface — ISO format YYYY-MM-DD",
      "credit_local: decimal string — never a float",
      "credit_reporting and credit_reporting_currency_code are SERVER-GENERATED — do not include in CSV (D-01)",
      "product_family_code: optional — supply when tenant quota is product-family-dimensional. Absent = sentinel (credit matches all-pf sentinel quota rows). Must match an active product_family record for this tenant.",
      "product_line_code: optional — supply when tenant quota is product-line-dimensional. Absent = sentinel '__ALL__'. Free-form varchar — no FK resolution. Must match the product_line_code value used on the matching quota row.",
    ],
  },
  ramp_schedule: {
    required: ["role_code", "ramp_month", "ramp_factor", "effective_date"],
    optional: [],
    notes: [
      "ramp_month: positive integer (month number from hire date, e.g. 1, 2, 3…)",
      "ramp_factor: decimal string between 0 and 1 (e.g. \"0.5000\" = 50% ramp)",
      "effective_date: ISO format YYYY-MM-DD — must not be in the future",
      "Mixed role_code and effective_date values are accepted in one file",
      "Warning shown if the same role_code appears with more than one effective_date",
    ],
  },
  plan_assignment: {
    required: ["employee_id", "plan_code", "effective_date", "fiscal_period_code", "business_unit_code"],
    optional: [],
    notes: [
      "employee_id resolves to position_id — position must exist and be active",
      "plan_code must reference an approved plan",
      "effective_date: ISO format YYYY-MM-DD",
      "fiscal_period_code: matches period_code in the fiscal_period table",
      "business_unit_code: must match an active business unit",
    ],
  },
  // C13 S955 — FX rate CSV import contract (POST /api/v1/exchange-rates/import)
  // Columns match fx_router.py POST /exchange-rates/import CSV parsing.
  // rate_source='ecb' is RESERVED — rejected server-side (FX_SOURCE_RESERVED).
  // All imported rows are Pending Approval — CA-Two must approve via FXRateManager.
  exchange_rate: {
    required: ["from_currency_code", "to_currency_code", "rate", "effective_date"],
    optional: ["rate_source"],
    notes: [
      "from_currency_code: local currency ISO 4217 code (e.g. JPY, GBP, AUD)",
      "to_currency_code: base currency — must match tenant reporting_currency_code (e.g. USD)",
      "rate: decimal string — local→base rate (e.g. 0.00671 for JPY→USD)",
      "effective_date: ISO format YYYY-MM-DD — must match fiscal period start date (e.g. 2026-01-01)",
      "rate_source: optional — 'manual' or 'client_internal'. Do NOT use 'ecb' (reserved — rejected).",
      "All imported rows are Pending Approval. CA-Two must approve via FX Rates approval surface.",
      "Dedup: SHA-256 hash of from|to|rate|effective_date. Approved rows are never overwritten.",
    ],
  },
  aop_input: {
    required: ["role_code", "segment_code", "business_unit_code", "planned_variable_comp", "planned_headcount", "currency_code"],
    optional: [],
    notes: [
      "fiscal_period_code is file-level — supply once as a column on every row",
      "planned_variable_comp: decimal string — total variable comp budget for this role/segment/BU",
      "planned_headcount: positive integer",
      "currency_code: ISO 4217 three-letter code (e.g. USD, GBP, EUR)",
    ],
  },
  // S914 G-02 — MBO results upload (OI-S910-TIER2-IMPORT-UI-SURFACE-MISSING)
  // S915 fix: result_value/scoring_method removed — not in _REQUIRED_TARGETS.
  // scoring_method is resolved server-side from mbo_definition (not customer-supplied).
  // achievement_pct / achieved_flag are conditional on scoring_method — optional at mapping layer.
  mbo_result: {
    required: ["employee_id", "fiscal_period_code", "plan_code", "mbo_code"],
    optional: ["achievement_pct", "achieved_flag", "result_notes"],
    notes: [
      "MBO objectives must be authored in the plan (DESIGN → Plan → Adjustments → MBO) before results can be loaded",
      "mbo_code must match a defined objective for the position's plan component",
      "scoring_method: binary | tiered — must match the objective's configured scoring_method",
      "achievement_pct: required for tiered objectives — attainment percentage as decimal (e.g. 0.85 = 85%)",
      "achieved_flag: required for binary objectives — true/false/1/0/yes/no",
      "Results must be loaded before Readiness check and calculation for any plan with mbo_weight > 0",
      "AD-106: mbo_router CRUD endpoints are not used here — this loads transactional result data only",
    ],
  },
  // S930 G-17 — NPS result upload
  // Mirrors mbo_result pattern. nps_score validated server-side in range [-100, 100].
  // nps_tier authoring: DESIGN → Plan → Adjustments → NPS (plan_authoring_router EP2b).
  // AD-S929-01: nps_router POST endpoints not used here — transactional result data only.
  nps_result: {
    required: ["employee_id", "fiscal_period_code", "nps_score"],
    optional: ["result_notes"],
    notes: [
      "NPS score bands must be authored in the plan (DESIGN → Plan → Adjustments → NPS) before results can be loaded",
      "nps_score: numeric in range [-100, 100] — e.g. 42 or -15.5",
      "One row per position per fiscal period — reimport is safe (upsert semantics)",
      "Results must be loaded before calculation for any plan with nps_weight > 0",
      "AD-S929-01: nps_router CRUD endpoints are not used here — this loads transactional result data only",
    ],
  },
  // S930 G-18 — Adoption result upload
  // Mirrors mbo_result pattern. milestone_code resolved server-side to adoption_milestone_id.
  // achievement_value is NOT NULL in DDL — required in CSV; rows missing it are rejected.
  // achieved_flag is nullable in DDL — optional, include when the milestone has a binary pass/fail signal.
  // Milestone authoring: DESIGN → Plan → Adjustments → Adoption (plan_authoring_router EP2b).
  // AD-S929-01: adoption_router POST endpoints not used here — transactional result data only.
  adoption_result: {
    required: ["employee_id", "fiscal_period_code", "milestone_code", "achievement_value"],
    optional: ["achieved_flag", "result_notes"],
    notes: [
      "Adoption milestones must be authored in the plan (DESIGN → Plan → Adjustments → Adoption) before results can be loaded",
      "milestone_code: must match an active milestone for this tenant (e.g. Q1_ONBOARD)",
      "achievement_value: required decimal — the measured outcome value (e.g. units completed, score, percentage)",
      "achieved_flag: optional — true/false/1/0/yes/no — supply when the milestone has a binary pass/fail outcome",
      "One row per position per milestone per fiscal period — reimport is safe (upsert semantics)",
      "Results must be loaded before calculation for any plan with adoption_weight > 0",
      "AD-S929-01: adoption_router CRUD endpoints are not used here — this loads transactional result data only",
    ],
  },
};

// Column mapping definitions — fallback defaults when no saved mapping exists.
// Mirrors import_service.py _REQUIRED_TARGETS. Used to seed a first-time mapping.
const COLUMN_MAPPINGS = {
  position: [
    { source_column_name: "employee_id",        target_column_name: "employee_id",        is_required: true  },
    { source_column_name: "full_name",           target_column_name: "full_name",           is_required: true  },
    { source_column_name: "role_code",           target_column_name: "role_code",           is_required: true  },
    { source_column_name: "segment_code",        target_column_name: "segment_code",        is_required: true  },
    { source_column_name: "hire_date",           target_column_name: "hire_date",           is_required: true  },
    { source_column_name: "is_active",           target_column_name: "is_active",           is_required: true  },
    { source_column_name: "ramp_start_date",     target_column_name: "ramp_start_date",     is_required: false },
    { source_column_name: "manager_employee_id", target_column_name: "manager_employee_id", is_required: false },
    { source_column_name: "territory_code",      target_column_name: "territory_code",      is_required: false },
    { source_column_name: "calculation_mode",    target_column_name: "calculation_mode",    is_required: false },
  ],
  ote_history: [
    { source_column_name: "employee_id",       target_column_name: "employee_id",       is_required: true  },
    { source_column_name: "ote_amount",        target_column_name: "ote_amount",        is_required: true  },
    { source_column_name: "ote_currency_code", target_column_name: "ote_currency_code", is_required: true  },
    { source_column_name: "effective_date",    target_column_name: "effective_date",    is_required: true  },
    { source_column_name: "end_date",          target_column_name: "end_date",          is_required: true  },
    { source_column_name: "ote_period_type",   target_column_name: "ote_period_type",   is_required: true  },
  ],
  rate_table_shell: [
    { source_column_name: "rate_table_code",        target_column_name: "rate_table_code",        is_required: true },
    { source_column_name: "rate_table_name",        target_column_name: "rate_table_name",        is_required: true },
    { source_column_name: "plan_code",              target_column_name: "plan_code",              is_required: true },
    { source_column_name: "role_code",              target_column_name: "role_code",              is_required: true },
    { source_column_name: "segment_code",           target_column_name: "segment_code",           is_required: true },
    { source_column_name: "default_ceiling_pct",    target_column_name: "default_ceiling_pct",    is_required: true },
    { source_column_name: "attainment_ceiling_pct", target_column_name: "attainment_ceiling_pct", is_required: true },
    { source_column_name: "effective_date",         target_column_name: "effective_date",         is_required: true },
  ],
  rate_table_row: [
    { source_column_name: "rate_table_code", target_column_name: "rate_table_code", is_required: true },
    { source_column_name: "from_pct",        target_column_name: "from_pct",        is_required: true },
    { source_column_name: "to_pct",          target_column_name: "to_pct",          is_required: true },
    { source_column_name: "payout_pct",      target_column_name: "payout_pct",      is_required: true },
    { source_column_name: "interpolation",   target_column_name: "interpolation",   is_required: true },
  ],
  plan: [
    { source_column_name: "plan_code",          target_column_name: "plan_code",          is_required: true  },
    { source_column_name: "plan_name",          target_column_name: "plan_name",          is_required: true  },
    { source_column_name: "role_code",          target_column_name: "role_code",          is_required: true  },
    { source_column_name: "segment_code",       target_column_name: "segment_code",       is_required: true  },
    { source_column_name: "fiscal_period_code", target_column_name: "fiscal_period_code", is_required: true  },
    { source_column_name: "ote_min",            target_column_name: "ote_min",            is_required: true  },
    { source_column_name: "ote_midpoint",       target_column_name: "ote_midpoint",       is_required: true  },
    { source_column_name: "ote_max",            target_column_name: "ote_max",            is_required: true  },
    { source_column_name: "ote_currency_code",  target_column_name: "ote_currency_code",  is_required: true  },
    { source_column_name: "base_pct",           target_column_name: "base_pct",           is_required: true  },
    { source_column_name: "variable_pct",       target_column_name: "variable_pct",       is_required: true  },
    { source_column_name: "arr_weight",         target_column_name: "arr_weight",         is_required: true  },
    { source_column_name: "mbo_weight",         target_column_name: "mbo_weight",         is_required: true  },
    { source_column_name: "nps_weight",         target_column_name: "nps_weight",         is_required: true  },
    { source_column_name: "adoption_weight",    target_column_name: "adoption_weight",    is_required: true  },
    { source_column_name: "rollup_mode",        target_column_name: "rollup_mode",        is_required: false },
    { source_column_name: "effective_date",     target_column_name: "effective_date",     is_required: true  },
    { source_column_name: "business_unit_code", target_column_name: "business_unit_code", is_required: false },
  ],
  quota: [
    { source_column_name: "fiscal_period_code",       target_column_name: "fiscal_period_code",       is_required: true  },
    { source_column_name: "quota_local",               target_column_name: "quota_local",               is_required: true  },
    { source_column_name: "quota_local_currency_code", target_column_name: "quota_local_currency_code", is_required: true  },
    { source_column_name: "employee_id",               target_column_name: "employee_id",               is_required: false },
    { source_column_name: "person_uuid",               target_column_name: "person_uuid",               is_required: false },
    { source_column_name: "business_unit_code",        target_column_name: "business_unit_code",        is_required: false },
  ],
  credit: [
    { source_column_name: "employee_id",                    target_column_name: "employee_id",                    is_required: true  },
    { source_column_name: "source_reference_id",            target_column_name: "source_reference_id",            is_required: true  },
    { source_column_name: "selling_motion",                 target_column_name: "selling_motion",                 is_required: true  },
    { source_column_name: "business_unit_code",             target_column_name: "business_unit_code",             is_required: true  },
    { source_column_name: "fiscal_period_code",             target_column_name: "fiscal_period_code",             is_required: true  },
    { source_column_name: "credit_local",               target_column_name: "credit_local",               is_required: true  },
    { source_column_name: "credit_local_currency_code", target_column_name: "credit_local_currency_code", is_required: true  },
    { source_column_name: "revenue_type",               target_column_name: "revenue_type",               is_required: false }, // S958: optional — required by engine when RTD enabled on plan
    { source_column_name: "deal_close_date",            target_column_name: "deal_close_date",            is_required: false },
  ],
  ramp_schedule: [
    { source_column_name: "role_code",      target_column_name: "role_code",      is_required: true },
    { source_column_name: "ramp_month",     target_column_name: "ramp_month",     is_required: true },
    { source_column_name: "ramp_factor",    target_column_name: "ramp_factor",    is_required: true },
    { source_column_name: "effective_date", target_column_name: "effective_date", is_required: true },
  ],
  plan_assignment: [
    { source_column_name: "employee_id",        target_column_name: "employee_id",        is_required: true },
    { source_column_name: "plan_code",          target_column_name: "plan_code",          is_required: true },
    { source_column_name: "effective_date",     target_column_name: "effective_date",     is_required: true },
    { source_column_name: "fiscal_period_code", target_column_name: "fiscal_period_code", is_required: true },
    { source_column_name: "business_unit_code", target_column_name: "business_unit_code", is_required: true },
  ],
  // S914 G-02 — S915 fix: aligned to _REQUIRED_TARGETS["mbo_result"] + _validate_mbo_result_rows.
  // plan_code added (required). result_value/scoring_method removed (not customer-supplied).
  // achievement_pct/achieved_flag added as optional (conditional on scoring_method per row).
  mbo_result: [
    { source_column_name: "employee_id",        target_column_name: "employee_id",        is_required: true  },
    { source_column_name: "fiscal_period_code", target_column_name: "fiscal_period_code", is_required: true  },
    { source_column_name: "plan_code",          target_column_name: "plan_code",          is_required: true  },
    { source_column_name: "mbo_code",           target_column_name: "mbo_code",           is_required: true  },
    { source_column_name: "achievement_pct",    target_column_name: "achievement_pct",    is_required: false },
    { source_column_name: "achieved_flag",      target_column_name: "achieved_flag",      is_required: false },
    { source_column_name: "result_notes",       target_column_name: "result_notes",       is_required: false },
  ],
  // S930 G-17 — aligned to _REQUIRED_TARGETS["nps_result"] + _validate_nps_result_rows.
  // nps_score required; result_notes optional. No secondary FK (no plan_code / mbo_code).
  nps_result: [
    { source_column_name: "employee_id",        target_column_name: "employee_id",        is_required: true  },
    { source_column_name: "fiscal_period_code", target_column_name: "fiscal_period_code", is_required: true  },
    { source_column_name: "nps_score",          target_column_name: "nps_score",          is_required: true  },
    { source_column_name: "result_notes",       target_column_name: "result_notes",       is_required: false },
  ],
  // S930 G-18 — aligned to _REQUIRED_TARGETS["adoption_result"] + _validate_adoption_result_rows.
  // milestone_code resolves server-side to adoption_milestone_id (customer never supplies the int PK).
  // achievement_value is NN in DDL — required. achieved_flag nullable — optional.
  adoption_result: [
    { source_column_name: "employee_id",        target_column_name: "employee_id",        is_required: true  },
    { source_column_name: "fiscal_period_code", target_column_name: "fiscal_period_code", is_required: true  },
    { source_column_name: "milestone_code",     target_column_name: "milestone_code",     is_required: true  },
    { source_column_name: "achievement_value",  target_column_name: "achievement_value",  is_required: true  },
    { source_column_name: "achieved_flag",      target_column_name: "achieved_flag",      is_required: false },
    { source_column_name: "result_notes",       target_column_name: "result_notes",       is_required: false },
  ],
  aop_input: [
    { source_column_name: "role_code",             target_column_name: "role_code",             is_required: true },
    { source_column_name: "segment_code",          target_column_name: "segment_code",          is_required: true },
    { source_column_name: "business_unit_code",    target_column_name: "business_unit_code",    is_required: true },
    { source_column_name: "planned_variable_comp", target_column_name: "planned_variable_comp", is_required: true },
    { source_column_name: "planned_headcount",     target_column_name: "planned_headcount",     is_required: true },
    { source_column_name: "currency_code",         target_column_name: "currency_code",         is_required: true },
    { source_column_name: "fiscal_period_code",    target_column_name: "fiscal_period_code",    is_required: true },
  ],
  // C13 S955 — FX rate CSV import column mapping
  exchange_rate: [
    { source_column_name: "from_currency_code", target_column_name: "from_currency_code", is_required: true  },
    { source_column_name: "to_currency_code",   target_column_name: "to_currency_code",   is_required: true  },
    { source_column_name: "rate",               target_column_name: "rate",               is_required: true  },
    { source_column_name: "effective_date",     target_column_name: "effective_date",     is_required: true  },
    { source_column_name: "rate_source",        target_column_name: "rate_source",        is_required: false },
  ],
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Fetch saved column mapping rows for (source_system, file_type).
 * Returns array of { column_mapping_id, source_column_name, target_column_name, is_required, is_active }.
 * Falls back to COLUMN_MAPPINGS defaults if server returns empty or errors.
 */
async function fetchColumnMapping(fileType) {
  try {
    const resp = await fetch(
      `/api/v1/import/column-mapping?source_system=${SOURCE_SYSTEM}&file_type=${fileType}`,
      { credentials: "include" },
    );
    if (!resp.ok) throw new Error("fetch failed");
    const data = await resp.json();
    const rows = Array.isArray(data) ? data : data?.mappings ?? [];
    if (rows.length > 0) {
      // Merge saved rows with COLUMN_MAPPINGS defaults.
      // Any default field missing from saved rows is appended as a new unsaved row.
      // This ensures fields added to COLUMN_MAPPINGS after initial save are surfaced.
      const savedSourceNames = new Set(rows.map((r) => r.source_column_name));
      const defaults = COLUMN_MAPPINGS[fileType] ?? [];
      const missing = defaults.filter((d) => !savedSourceNames.has(d.source_column_name));
      const appended = missing.map((m, i) => ({
        column_mapping_id: -(rows.length + i + 1), // negative sentinel — not yet persisted
        source_column_name: m.source_column_name,
        target_column_name: m.target_column_name,
        is_required: m.is_required,
        is_active: true,
      }));
      return [...rows, ...appended];
    }
  } catch { /* fall through to defaults */ }
  // No saved mapping — return defaults so the UI can seed a first-time mapping
  return (COLUMN_MAPPINGS[fileType] ?? []).map((m, i) => ({
    column_mapping_id: -(i + 1), // negative sentinel — not yet persisted
    source_column_name: m.source_column_name,
    target_column_name: m.target_column_name,
    is_required: m.is_required,
    is_active: true,
  }));
}

/**
 * Persist the confirmed mapping set for (source_system, file_type).
 * Non-blocking — called after user confirms mapping, before upload.
 */
async function saveColumnMapping(fileType, mappingRows) {
  try {
    await fetch("/api/v1/import/column-mapping", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source_system: SOURCE_SYSTEM,
        file_type: fileType,
        mappings: mappingRows.map(({ source_column_name, target_column_name, is_required }) => ({
          source_column_name,
          target_column_name,
          is_required: is_required ?? false,
        })),
      }),
    });
  } catch { /* non-blocking */ }
}

/**
 * Apply a mapping to raw CSV headers, returning canonical header names.
 * source_column_name (lowercased) → target_column_name.
 * Headers not in the mapping pass through unchanged.
 */
function applyMappingToHeaders(rawHeaders, mappingRows) {
  const lookup = {};
  for (const row of mappingRows) {
    lookup[row.source_column_name.toLowerCase()] = row.target_column_name;
  }
  return rawHeaders.map((h) => lookup[h.toLowerCase()] ?? h);
}

async function parseCsvFile(file) {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (!lines.length) throw new Error("File appears to be empty.");
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
  return { headers, dataRowCount: Math.max(0, lines.length - 1) };
}

function validateHeaders(headers, fileType) {
  const contract = FIELD_CONTRACTS[fileType];
  if (!contract) return [];
  return contract.required.filter((col) => !headers.includes(col));
}

function fmt(n) { return (n ?? 0).toLocaleString(); }

function friendlyApprovalError(code, fallback) {
  const MAP = {
    SELF_APPROVAL_PROHIBITED: "You cannot approve records you created. A second comp admin must approve.",
    PLAN_ALREADY_APPROVED:    "These plans have already been approved.",
    PLAN_SESSION_NOT_FOUND:   "Session ID not found. Verify the ID and try again.",
  };
  return MAP[code] ?? fallback ?? "Approval failed. Please try again.";
}

// ── Shared UI components ──────────────────────────────────────────────────────

function TabBar({ tabs, activeTab, onTabChange }) {
  return (
    <div className="flex gap-0 border-b-2 border-[rgb(var(--color-border))] overflow-x-auto">
      {tabs.map(({ id, label, oi }) => (
        <button
          key={id}
          type="button"
          onClick={() => onTabChange(id)}
          className={[
            "relative whitespace-nowrap px-4 py-2.5 text-tok-sm -mb-0.5 border-b-2 transition-colors",
            activeTab === id
              ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))] font-semibold"
              : "border-transparent text-[rgb(var(--color-text-muted))] font-medium hover:text-[rgb(var(--color-text))] hover:border-[rgb(var(--color-border))]",
          ].join(" ")}
        >
          {label}
          {oi && (
            <span
              className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[rgb(var(--color-warning))] align-middle"
              title="Coming soon — use Excel Add-in"
            />
          )}
        </button>
      ))}
    </div>
  );
}

function SubTabBar({ subTabs, activeSubTab, onSubTabChange }) {
  return (
    <div className="flex gap-0 border-b border-[rgb(var(--color-border))] mb-5">
      {subTabs.map(({ id, label, oi }) => (
        <button
          key={id}
          type="button"
          onClick={() => onSubTabChange(id)}
          className={[
            "whitespace-nowrap px-4 py-2 text-tok-xs -mb-px border-b-2 transition-colors",
            activeSubTab === id
              ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))] font-semibold"
              : "border-transparent text-[rgb(var(--color-text-muted))] font-medium hover:text-[rgb(var(--color-text))]",
          ].join(" ")}
        >
          {label}
          {oi && (
            <span
              className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[rgb(var(--color-warning))] align-middle"
              title="Coming soon"
            />
          )}
        </button>
      ))}
    </div>
  );
}

/** 1 · Import | 2 · Approve panel switcher — solid filled style matching addin. */
function PanelSwitcher({ panels, activePanel, onPanelChange }) {
  return (
    <div className="flex rounded-lg border border-[rgb(var(--color-border))] overflow-hidden text-tok-xs font-medium w-fit">
      {panels.map((p, i) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onPanelChange(p.id)}
          className={[
            "px-4 py-1.5 transition-colors",
            i > 0 ? "border-l border-[rgb(var(--color-border))]" : "",
            activePanel === p.id
              ? "bg-[rgb(var(--color-primary))] text-white"
              : "bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]",
          ].join(" ")}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function FieldContractCard({ fileType }) {
  const contract = FIELD_CONTRACTS[fileType];
  if (!contract) return null;
  return (
    <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-2.5">
      <div>
        <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide mb-1">Required fields</p>
        <p className="font-mono text-tok-xs text-[rgb(var(--color-text))] leading-relaxed break-all">{contract.required.join(", ")}</p>
      </div>
      {contract.optional.length > 0 && (
        <div>
          <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide mb-1">Optional fields</p>
          <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed break-all">{contract.optional.join(", ")}</p>
        </div>
      )}
      {contract.notes.length > 0 && (
        <div className="border-t border-[rgb(var(--color-border))] pt-2.5 space-y-1">
          {contract.notes.map((note, i) => (
            <p key={i} className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
              <span className="text-[rgb(var(--color-text))]">·</span> {note}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function RejectedRowsTable({ rows }) {
  if (!rows?.length) return null;
  const visible  = rows.slice(0, 50);
  const overflow = rows.length - 50;
  return (
    <div className="space-y-1.5">
      <p className="text-tok-xs font-semibold text-[rgb(var(--color-danger))]">Rejected rows</p>
      <div className="max-h-48 overflow-y-auto rounded-lg border border-[rgb(var(--color-danger))] border-opacity-30">
        <table className="w-full text-tok-xs">
          <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))] w-14">Row</th>
              <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))] w-32">Field</th>
              <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))] w-44">Error code</th>
              <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Message</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-[rgb(var(--color-surface))]" : "bg-[rgb(var(--color-surface-muted))]"}>
                <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">{row.row_number ?? "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[rgb(var(--color-text))]">{row.field ?? "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[rgb(var(--color-danger))]">{row.error_code ?? row.code ?? "—"}</td>
                <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">{row.message ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {overflow > 0 && (
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          and {fmt(overflow)} more — fix the CSV and re-upload to see remaining errors
        </p>
      )}
    </div>
  );
}

function ImportResultCard({ result, voidedCount = 0, isFeedEntity = false }) {
  if (!result) return null;
  const { status, json } = result;

  if (status === 200) {
    const log = json?.import_log ?? {};
    return (
      <div className="rounded-lg border border-[rgb(var(--color-success))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-2">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))] shrink-0" />
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">Import complete</p>
        </div>
        <div className="flex gap-5 flex-wrap">
          {log.rows_inserted  != null && <Stat label="Inserted"  value={log.rows_inserted}  color="success" />}
          {log.rows_updated   != null && <Stat label="Updated"   value={log.rows_updated}   color="success" />}
          {log.rows_unchanged != null && <Stat label="Unchanged" value={log.rows_unchanged} />}
          {isFeedEntity && voidedCount > 0 && <Stat label="Retired" value={voidedCount} color="danger" />}
        </div>
      </div>
    );
  }

  if (status === 207) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[rgb(var(--color-warning))] shrink-0" />
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">Partial import</p>
          </div>
          <div className="flex gap-5">
            {json?.rows_loaded    != null && <Stat label="Loaded"    value={json.rows_loaded}    color="success" />}
            {json?.rows_unchanged != null && <Stat label="Unchanged" value={json.rows_unchanged} />}
            {json?.rows_rejected  != null && <Stat label="Rejected"  value={json.rows_rejected}  color="danger"  />}
          </div>
          {json?.error_summary && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{json.error_summary}</p>}
        </div>
        <RejectedRowsTable rows={json?.rejected_rows} />
      </div>
    );
  }

  // 422 or network error
  const errCode  = json?.error?.code ?? json?.error ?? json?.detail?.error?.code;
  const errMsg   = json?.error?.message ?? json?.message ?? json?.detail?.error?.message;
  const rejected = json?.error?.rejected_rows ?? json?.rejected_rows ?? [];
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-[rgb(var(--color-danger))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-1.5">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-[rgb(var(--color-danger))] shrink-0" />
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">Import failed</p>
        </div>
        {errCode && <p className="font-mono text-tok-xs text-[rgb(var(--color-danger))]">{errCode}</p>}
        {errMsg  && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{errMsg}</p>}
        {json?.error?.rows_rejected != null && (
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {fmt(json.error.rows_rejected)} row{json.error.rows_rejected !== 1 ? "s" : ""} rejected — no data written
          </p>
        )}
      </div>
      <RejectedRowsTable rows={rejected} />
    </div>
  );
}

function Stat({ label, value, color }) {
  const cls = color === "success" ? "text-[rgb(var(--color-success))]"
    : color === "danger" ? "text-[rgb(var(--color-danger))]"
    : "text-[rgb(var(--color-text))]";
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`text-tok-sm font-semibold tabular-nums ${cls}`}>{fmt(value)}</span>
      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</span>
    </div>
  );
}

// S1003 OI-IMPORT-HISTORY-ENDPOINT-SWITCH:
// Switched from GET /api/v1/import-logs (old) to GET /api/v1/import/history (new).
// New endpoint adds: feed_name, rows_voided, requires_decision, decision_due_at.
// Added: Retired column, ⏳ pending indicator, Resolve link → ReturnJourneyPanel.
// Added: Retired count link → VoidedRecordsPanel slide-in drawer.
// isFeedEntity: feed_name and voided columns shown only for quota/credit tabs.
// All other tabs: backward-compatible (new endpoint returns same base fields).
function ImportHistoryCard({ fileType, historyKey, isFeedEntity, onResolvePending, onViewVoided }) {
  const [logs, setLogs]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    // S1003: switched to new /history endpoint for feed_name, rows_voided, requires_decision
    fetch(`/api/v1/import/history?file_type=${fileType}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) {
          const rows = Array.isArray(d) ? d : d?.history ?? d?.logs ?? d?.data ?? [];
          setLogs(rows.slice(0, 10));
        }
      })
      .catch(() => { if (!cancelled) setError("Could not load import history."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fileType, historyKey]);

  return (
    <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
        <Clock className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
        <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Import history</p>
      </div>
      {loading && <p className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">Loading…</p>}
      {error   && <p className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">{error}</p>}
      {!loading && !error && !logs?.length && <p className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">No imports yet.</p>}
      {!loading && !error && logs?.length > 0 && (
        <div className="max-h-48 overflow-y-auto">
          <table className="w-full text-tok-xs">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Date</th>
                {isFeedEntity && (
                  <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Feed</th>
                )}
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Status</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Loaded</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Unchanged</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Rejected</th>
                {isFeedEntity && (
                  <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Retired</th>
                )}
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">File</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => {
                const isPending   = log.requires_decision || log.load_status === "pending";
                // S1011 OI-IMPORT-PENDING-EXPIRY: overdue when backend flag set.
                const isOverdue   = isPending && log.is_overdue;
                const voidedCount = log.rows_voided ?? 0;
                return (
                  <tr key={log.import_log_id ?? i} className={i % 2 === 0 ? "bg-[rgb(var(--color-surface))]" : "bg-[rgb(var(--color-surface-muted))]"}>
                    <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                    </td>
                    {isFeedEntity && (
                      <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))] truncate max-w-[100px]" title={log.feed_name}>
                        {log.feed_name ?? "—"}
                      </td>
                    )}
                    <td className="px-3 py-1.5">
                      {isPending ? (
                        <button
                          type="button"
                          onClick={() => onResolvePending?.(log)}
                          className={`flex items-center gap-1 hover:underline ${isOverdue ? "text-red-600" : "text-amber-600"}`}
                          title={isOverdue ? "Decision overdue — click to resolve" : "Click to resolve this pending decision"}
                        >
                          {isOverdue ? "🔴" : "⏳"} <span>{isOverdue ? "Overdue" : "Pending"}</span>
                          {isOverdue && log.decision_due_at && (
                            <span className="text-red-400 ml-1">
                              (due {new Date(log.decision_due_at).toLocaleDateString()})
                            </span>
                          )}
                        </button>
                      ) : (
                        <span className={
                          log.load_status === "loaded" || log.load_status === "kept"
                            ? "text-[rgb(var(--color-success))]"
                            : log.load_status === "partial"
                            ? "text-amber-600"
                            : "text-[rgb(var(--color-text-muted))]"
                        }>
                          {log.load_status ?? "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-[rgb(var(--color-success))]">
                      {fmt(log.rows_inserted ?? log.rows_loaded)}
                    </td>
                    <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">
                      {fmt(log.rows_unchanged)}
                    </td>
                    <td className={`px-3 py-1.5 ${(log.rows_rejected ?? 0) > 0 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-text-muted))]"}`}>
                      {(log.rows_rejected ?? 0) > 0 ? fmt(log.rows_rejected) : "—"}
                    </td>
                    {isFeedEntity && (
                      <td className="px-3 py-1.5">
                        {voidedCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => onViewVoided?.(log.import_log_id)}
                            className="text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-primary))] hover:underline"
                            title="View retired records"
                          >
                            {fmt(voidedCount)}
                          </button>
                        ) : (
                          <span className="text-[rgb(var(--color-text-muted))]">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))] truncate max-w-[140px]" title={log.source_filename}>
                      {log.source_filename ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── PendingDecisionBanner ─────────────────────────────────────────────────────
// S1003 OI-IMPORT-PENDING-DECISION-BANNER
// Persistent banner when pending imports exist for current period.
// Shown at top of quota/credit tabs (isFeedEntity only).
// Reads from GET /api/v1/import/history?file_type=X filtered by load_status=pending.
function PendingDecisionBanner({ fileType, onResolve }) {
  const [pendingCount, setPendingCount] = useState(0);
  // S1011 OI-IMPORT-PENDING-EXPIRY: track overdue separately for stronger visual.
  const [overdueCount, setOverdueCount] = useState(0);
  const [earliestDue, setEarliestDue]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/v1/import/history?file_type=${fileType}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const rows = Array.isArray(d) ? d : d?.history ?? [];
        const pending  = rows.filter((r) => r.load_status === "pending" || r.requires_decision);
        const overdue  = pending.filter((r) => r.is_overdue);
        const dueDates = pending
          .map((r) => r.decision_due_at)
          .filter(Boolean)
          .sort();
        setPendingCount(pending.length);
        setOverdueCount(overdue.length);
        setEarliestDue(dueDates[0] ?? null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [fileType]);

  if (pendingCount === 0) return null;

  const isOverdue = overdueCount > 0;
  const borderCls = isOverdue
    ? "border-red-300 bg-red-50"
    : "border-amber-300 bg-amber-50";
  const textCls   = isOverdue ? "text-red-800"  : "text-amber-800";
  const subCls    = isOverdue ? "text-red-700"  : "text-amber-700";
  const btnCls    = isOverdue
    ? "border-red-400 bg-red-100 text-red-800 hover:bg-red-200"
    : "border-amber-400 bg-amber-100 text-amber-800 hover:bg-amber-200";
  const icon      = isOverdue ? "🔴" : "⏳";

  const dueLine = earliestDue
    ? ` Decision due: ${new Date(earliestDue).toLocaleDateString()}.`
    : "";

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${borderCls}`}
    >
      <span className="text-base shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-tok-sm font-semibold ${textCls}`}>
          {isOverdue
            ? `${overdueCount} import${overdueCount !== 1 ? "s are" : " is"} overdue for a decision.`
            : `${pendingCount} import${pendingCount !== 1 ? "s have" : " has"} an unresolved decision for this period.`
          }
        </p>
        <p className={`text-tok-xs mt-0.5 ${subCls}`}>
          {isOverdue
            ? `Absent records are still active but the decision window has passed.${dueLine} Resolve before this period is certified.`
            : `Absent records are kept active until you decide.${dueLine} Resolve before this period is certified.`
          }
        </p>
      </div>
      <button
        type="button"
        onClick={onResolve}
        className={`shrink-0 rounded-lg border px-3 py-1.5 text-tok-xs font-semibold transition-colors ${btnCls}`}
      >
        View in history ↓
      </button>
    </div>
  );
}

// ── VoidedRecordsPanel ────────────────────────────────────────────────────────
// S1003 OI-IMPORT-VOIDED-RECORDS-PANEL
// Slide-in drawer showing records retired by a specific finalise operation.
// Triggered from Retired count link in ImportHistoryCard.
// Reads GET /api/v1/import/voided-records?import_log_id=uuid (paginated 100/page).
// Spec: UnifiedImportSpec_v1_5.md §5.6
function VoidedRecordsPanel({ importLogId, fileType, onClose }) {
  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [hasMore, setHasMore]   = useState(false);
  const [cursor, setCursor]     = useState(null);
  const [totalVoided, setTotalVoided] = useState(0);

  const loadRecords = useCallback(async (nextCursor = null) => {
    setLoading(true); setError(null);
    try {
      const url = `/api/v1/import/voided-records?import_log_id=${importLogId}`
        + (nextCursor ? `&cursor=${nextCursor}` : "");
      const r = await fetch(url, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) { setError("Could not load retired records."); return; }
      setRecords((prev) => nextCursor ? [...prev, ...(d.records ?? [])] : (d.records ?? []));
      setHasMore(d.has_more ?? false);
      setCursor(d.next_cursor ?? null);
      setTotalVoided(d.rows_voided ?? 0);
    } catch { setError("Could not load retired records."); }
    finally { setLoading(false); }
  }, [importLogId]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const valueKey   = fileType === "credit" ? "credit_local" : "quota_local";
  const valueLabel = fileType === "credit" ? "Credit" : "Quota";

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-2xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Retired Records</p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
            {fmt(totalVoided)} record{totalVoided !== 1 ? "s" : ""} retired — retained for audit, excluded from calculations.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-border))] transition-colors"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {loading && records.length === 0 && (
          <p className="px-5 py-4 text-tok-xs text-[rgb(var(--color-text-muted))]">Loading…</p>
        )}
        {error && (
          <p className="px-5 py-4 text-tok-xs text-[rgb(var(--color-danger))]">{error}</p>
        )}
        {!loading && !error && records.length === 0 && (
          <p className="px-5 py-4 text-tok-xs text-[rgb(var(--color-text-muted))]">No retired records found.</p>
        )}
        {records.length > 0 && (
          <table className="w-full text-tok-xs">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Employee</th>
                <th className="px-4 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">BU</th>
                <th className="px-4 py-2 text-right font-semibold text-[rgb(var(--color-text-muted))]">{valueLabel}</th>
                <th className="px-4 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Retired at</th>
              </tr>
            </thead>
            <tbody>
              {records.map((rec, i) => (
                <tr key={rec.credit_id ?? rec.quota_id ?? i} className={i % 2 === 0 ? "bg-[rgb(var(--color-surface))]" : "bg-[rgb(var(--color-surface-muted))]"}>
                  <td className="px-4 py-1.5 text-[rgb(var(--color-text))]">{rec.employee_id ?? "—"}</td>
                  <td className="px-4 py-1.5 text-[rgb(var(--color-text-muted))]">{rec.business_unit_code ?? "—"}</td>
                  <td className="px-4 py-1.5 text-right text-[rgb(var(--color-text))]">{rec[valueKey] ?? "—"}</td>
                  <td className="px-4 py-1.5 text-[rgb(var(--color-text-muted))]">
                    {rec.voided_at ? new Date(rec.voided_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {hasMore && !loading && (
          <div className="px-5 py-3 border-t border-[rgb(var(--color-border))]">
            <button
              type="button"
              onClick={() => loadRecords(cursor)}
              className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline"
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ReturnJourneyPanel ────────────────────────────────────────────────────────
// S1003 OI-IMPORT-WEBAPP-RETURN-JOURNEY
// Modal to resolve a pending import from Import History.
// POST /api/v1/import/finalise with mode='keep' | 'retire' | 'pending'.
// Triggered by clicking ⏳ Pending in ImportHistoryCard.
// Spec: DecisionFlow_Import_UX_Spec_v1.md §4.8
function ReturnJourneyPanel({ log, onResolved, onClose }) {
  const [resolving, setResolving] = useState(false);
  const [error, setError]         = useState(null);

  const ap = (() => {
    try { return typeof log.analysis_payload === "string"
      ? JSON.parse(log.analysis_payload) : log.analysis_payload ?? {};
    } catch { return {}; }
  })();
  const absentRows = ap.absent_rows ?? 0;

  const handleResolve = useCallback(async (mode) => {
    setResolving(true); setError(null);
    try {
      const body = {
        import_log_id: log.import_log_id,
        mode,
        feed_id: log.feed_id,
      };
      const r = await fetch("/api/v1/import/finalise", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(d?.error?.message ?? d?.message ?? `Error ${r.status}`);
        return;
      }
      onResolved?.(mode, d);
    } catch (err) {
      setError(err.message ?? "Network error.");
    } finally {
      setResolving(false);
    }
  }, [log, onResolved]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="w-full max-w-md rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] shadow-2xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              Resolve pending import decision
            </p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
              {fmt(absentRows)} record{absentRows !== 1 ? "s are" : " is"} in the system but not in this file.
              What should happen to {absentRows !== 1 ? "them" : "it"}?
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-border))]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-xs text-[rgb(var(--color-danger))]">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <button
            type="button"
            disabled={resolving}
            onClick={() => handleResolve("keep")}
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-left hover:bg-[rgb(var(--color-border))] transition-colors disabled:opacity-50"
          >
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Keep them active</p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
              Prior records stay. Only new and changed records from the file are updated.
            </p>
          </button>

          <button
            type="button"
            disabled={resolving}
            onClick={() => handleResolve("retire")}
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-left hover:bg-[rgb(var(--color-border))] transition-colors disabled:opacity-50"
          >
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Retire them</p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
              This file is the complete dataset. Records not in the file will be retired and excluded from calculations.
            </p>
          </button>
        </div>

        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          {resolving ? "Resolving…" : "This decision cannot be undone without re-importing."}
        </p>
      </div>
    </div>
  );
}

function OiCalloutPanel({ entityLabel, oiId, description }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] px-4 py-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-[rgb(var(--color-warning))] shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{entityLabel} — webapp upload not yet available</p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">{description}</p>
            <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">{oiId}</p>
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-1">
        <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Workaround</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
          Open the DecisionFlow Excel Add-in → Workbench → <strong className="text-[rgb(var(--color-text))]">{entityLabel}</strong> tab.
          The same CSV schema is accepted on both surfaces.
        </p>
      </div>
    </div>
  );
}

// ── ColumnMappingStep ─────────────────────────────────────────────────────────
//
// Renders between file select and validate. Shows saved mapping rows from
// GET /api/v1/import/column-mapping, allows inline edit of source_column_name,
// and persists via POST before advancing to validate.
//
// Props:
//   fileType       {string}    e.g. "position"
//   rawHeaders     {string[]}  headers extracted from uploaded CSV (row 0)
//   mappingRows    {Array}     from fetchColumnMapping() — saved or default rows
//   onConfirm      {function}  called with confirmed mappingRows[]
//   onCancel       {function}  resets to IDLE

function ColumnMappingStep({ fileType, rawHeaders, mappingRows, onConfirm, onCancel }) {
  // Local editable copy of mapping rows: user can reassign source_column_name per row
  const [rows, setRows] = useState(() =>
    mappingRows.map((r) => ({ ...r, _edited_source: r.source_column_name }))
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Derive which required target fields are not yet covered by a mapped source
  const missingRequired = rows
    .filter((r) => r.is_required && !r._edited_source?.trim())
    .map((r) => r.target_column_name);

  const canConfirm = missingRequired.length === 0;

  const handleSourceChange = useCallback((idx, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, _edited_source: value } : r));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!canConfirm) return;
    setSaving(true);
    setSaveError(null);
    const confirmed = rows.map((r) => ({
      ...r,
      source_column_name: r._edited_source?.trim() || r.source_column_name,
    }));
    await saveColumnMapping(fileType, confirmed);
    setSaving(false);
    onConfirm(confirmed);
  }, [canConfirm, rows, fileType, onConfirm]);

  // Options for the source column selector: CSV headers + blank option
  const headerOptions = ["", ...rawHeaders];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Map columns</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
          Match your CSV column headers to the required fields. Saved mappings are pre-filled —
          update if your file uses different column names.
        </p>
      </div>

      {/* Missing required callout */}
      {missingRequired.length > 0 && (
        <div className="rounded-lg border border-[rgb(var(--color-danger))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-1">
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-[rgb(var(--color-danger))] shrink-0" />
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">Required fields not mapped</p>
          </div>
          <p className="font-mono text-tok-xs text-[rgb(var(--color-danger))] leading-relaxed">
            {missingRequired.join(", ")}
          </p>
        </div>
      )}

      {/* Mapping table */}
      <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-tok-xs">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))] w-8"></th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Your CSV column</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">DecisionFlow field</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isMissing = row.is_required && !row._edited_source?.trim();
                return (
                  <tr
                    key={row.target_column_name}
                    className={idx % 2 === 0 ? "bg-[rgb(var(--color-surface))]" : "bg-[rgb(var(--color-surface-muted))]"}
                  >
                    {/* Required badge */}
                    <td className="px-3 py-2 text-center">
                      {row.is_required && (
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full bg-[rgb(var(--color-primary))]"
                          title="Required"
                        />
                      )}
                    </td>
                    {/* Source column selector */}
                    <td className="px-3 py-1.5">
                      <select
                        value={row._edited_source ?? ""}
                        onChange={(e) => handleSourceChange(idx, e.target.value)}
                        className={[
                          "w-full rounded border px-2 py-1 text-tok-xs bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))]",
                          "focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]",
                          isMissing
                            ? "border-[rgb(var(--color-danger))]"
                            : "border-[rgb(var(--color-border))]",
                        ].join(" ")}
                      >
                        <option value="">— not mapped —</option>
                        {headerOptions.filter(Boolean).map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </td>
                    {/* Target field (canonical — read-only) */}
                    <td className="px-3 py-1.5">
                      <span className="font-mono text-[rgb(var(--color-text))]">{row.target_column_name}</span>
                      {!row.is_required && (
                        <span className="ml-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">optional</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {saveError && (
        <p className="text-tok-xs text-[rgb(var(--color-danger))]">{saveError}</p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-border))] transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm || saving}
          className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {saving ? "Saving…" : "Confirm mapping"}
        </button>
      </div>
    </div>
  );
}

// ── EntityUploadPanel ─────────────────────────────────────────────────────────
// Four-step post S558: Choose file → Map columns → Validate → Import
// onImportSuccess(json) — called after 200/207, allows parent to react

const STEP = {
  IDLE:       "IDLE",
  FETCHING:   "FETCHING",    // loading saved mapping from server after file select
  MAPPING:    "MAPPING",     // user confirming/editing column mapping
  VALIDATING: "VALIDATING",
  VALIDATED:  "VALIDATED",
  UPLOADING:  "UPLOADING",
  // S1001 — quota/credit Analyse-then-Decide states (isFeedEntity only)
  ANALYSING:  "ANALYSING",   // POST /api/v1/import/analyse in flight
  CONFIRMING: "CONFIRMING",  // AbsentRowsConfirmation gate shown
  FINALISING: "FINALISING",  // POST /api/v1/import/finalise in flight
  PENDING:    "PENDING",     // quarantine — user chose Decide Later
  DONE:       "DONE",
};

// uploadUrl prop: override the POST endpoint. Default: /api/v1/import/upload.
// FX Rates tab uses /api/v1/exchange-rates/import?fiscal_period_id=<id> (AD-S954-02).
// S1001: feedEntityType flag enables Feed selector + Analyse-then-Decide gate
//   for quota and credit tabs. All other entity types are completely unchanged.
function EntityUploadPanel({ fileType, entityLabel, onImportSuccess, isLocked, period, uploadUrl }) {
  const fileInputRef = useRef(null);
  const isFeedEntity = FEED_ENTITY_TYPES.has(fileType); // S1001: quota/credit only
  const [step,           setStep]           = useState(STEP.IDLE);
  const [file,           setFile]           = useState(null);
  const [rawHeaders,     setRawHeaders]     = useState([]);
  const [mappingRows,    setMappingRows]    = useState([]);
  const [confirmedMap,   setConfirmedMap]   = useState([]);
  const [missingCols,    setMissingCols]    = useState([]);
  const [optionalAbsent, setOptionalAbsent] = useState([]);
  const [rowCount,       setRowCount]       = useState(null);
  const [result,         setResult]         = useState(null);
  const [historyKey,     setHistoryKey]     = useState(0);
  const [fetchError,     setFetchError]     = useState(null);
  // S1001 — Feed + Analyse-then-Decide state (quota/credit only, isFeedEntity guard)
  // AD-S1001-FEED-REGISTRY, AD-S1001-IMPORT-ANALYSE-THEN-DECIDE (LOCKED S1001)
  const [feeds,        setFeeds]        = useState([]);
  const [feedId,       setFeedId]       = useState(null);
  const [creatingFeed, setCreatingFeed] = useState(false);
  const [analysis,     setAnalysis]     = useState(null);   // ImportAnalysis result from /analyse
  const [importLogId,  setImportLogId]  = useState(null);   // import_log_id after /upload
  const [voidedCount,  setVoidedCount]  = useState(0);      // rows_voided from /finalise
  // S1012 OI-IMPORT-FEED-AUTO-RETIRE-UI: toggle state for auto_retire_absent PATCH.
  const [autoRetireLoading, setAutoRetireLoading] = useState(false);
  const [autoRetireError,   setAutoRetireError]   = useState(null);
  // S1012 OI-IMPORT-FEED-ARCHIVAL: archive state for is_active PATCH.
  const [archiveLoading,    setArchiveLoading]    = useState(false);
  const [archiveError,      setArchiveError]      = useState(null);
  // S1003: return journey + voided records panel state
  const [pendingLog,       setPendingLog]       = useState(null);   // log row for ReturnJourneyPanel
  const [voidedLogId,      setVoidedLogId]      = useState(null);   // import_log_id for VoidedRecordsPanel
  const contract = FIELD_CONTRACTS[fileType] ?? { required: [], optional: [], notes: [] };

  // S1001: Load feeds on mount for quota/credit tabs
  useEffect(() => {
    if (!isFeedEntity) return;
    let cancelled = false;
    fetch(`/api/v1/import/feeds?file_type=${fileType}&source_system=${SOURCE_SYSTEM}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setFeeds(d.feeds ?? []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [fileType, isFeedEntity]);

  useEffect(() => {
    setStep(STEP.IDLE); setFile(null); setRawHeaders([]); setMappingRows([]);
    setConfirmedMap([]); setMissingCols([]); setOptionalAbsent([]);
    setRowCount(null); setResult(null); setFetchError(null);
    setFeeds([]); setFeedId(null); setCreatingFeed(false);
    setAnalysis(null); setImportLogId(null); setVoidedCount(0);
    setAutoRetireLoading(false); setAutoRetireError(null);
    setArchiveLoading(false); setArchiveError(null);
    setPendingLog(null); setVoidedLogId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [fileType]);

  // S1012 OI-IMPORT-FEED-AUTO-RETIRE-UI: toggle auto_retire_absent on the selected feed.
  // RBAC gate lives in the router (tenant_super_admin only) — UI renders for all comp_admin
  // and surfaces the 403 as an inline error if the role is insufficient.
  // On success: update the feed object in feeds state so the toggle reflects immediately.
  const handleAutoRetireToggle = useCallback(async () => {
    const selectedFeed = feeds.find((f) => f.feed_id === feedId);
    if (!selectedFeed || autoRetireLoading) return;
    const newValue = !selectedFeed.auto_retire_absent;
    setAutoRetireLoading(true);
    setAutoRetireError(null);
    try {
      const resp = await fetch(`/api/v1/import/feeds/${feedId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ auto_retire_absent: newValue }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (resp.status === 403) {
          setAutoRetireError("Requires tenant_super_admin role to change this setting.");
        } else {
          setAutoRetireError(data?.detail?.message ?? data?.detail ?? "Failed to update feed.");
        }
        return;
      }
      // Update feed in local state — reflects the value returned by the router
      setFeeds((prev) =>
        prev.map((f) => (f.feed_id === feedId ? { ...f, auto_retire_absent: data.auto_retire_absent } : f))
      );
    } catch (err) {
      setAutoRetireError(err.message ?? "Network error.");
    } finally {
      setAutoRetireLoading(false);
    }
  }, [feeds, feedId, autoRetireLoading]);

  // S1012 OI-IMPORT-FEED-ARCHIVAL: archive the selected feed (is_active → false).
  // RBAC gate in router (tenant_super_admin only) — 403 surfaced as inline error.
  // On success: remove feed from local feeds state and clear feedId selection.
  // All historical data loaded through this feed is fully preserved.
  const handleArchiveFeed = useCallback(async () => {
    if (!feedId || archiveLoading) return;
    setArchiveLoading(true);
    setArchiveError(null);
    try {
      const resp = await fetch(`/api/v1/import/feeds/${feedId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: false }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (resp.status === 403) {
          setArchiveError("Requires tenant_super_admin role to archive a feed.");
        } else {
          setArchiveError(data?.detail?.message ?? data?.detail ?? "Failed to archive feed.");
        }
        return;
      }
      // Remove archived feed from local list and clear selection
      setFeeds((prev) => prev.filter((f) => f.feed_id !== feedId));
      setFeedId(null);
    } catch (err) {
      setArchiveError(err.message ?? "Network error.");
    } finally {
      setArchiveLoading(false);
    }
  }, [feedId, archiveLoading]);

  // After file is chosen: parse headers then fetch saved mapping
  const handleFileChange = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setMissingCols([]);
    setOptionalAbsent([]);
    setRowCount(null);
    setFetchError(null);
    setStep(STEP.FETCHING);
    fetch("/api/v1/addin/telemetry", {
      method:      "POST",
      credentials: "include",
      headers:     { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: "addin_import_started",
        surface:    "webapp",
        duration_ms: null,
        metadata:   { entity_type: fileType },
      }),
    }).catch(() => {});
    try {
      const { headers, dataRowCount } = await parseCsvFile(f);
      setRawHeaders(headers);
      setRowCount(dataRowCount);
      const saved = await fetchColumnMapping(fileType);
      setMappingRows(saved);
      setStep(STEP.MAPPING);
    } catch (err) {
      setFetchError(err.message ?? "Could not read file.");
      setStep(STEP.IDLE);
    }
  }, [fileType]);

  // User confirmed mapping in ColumnMappingStep
  const handleMappingConfirm = useCallback((confirmed) => {
    setConfirmedMap(confirmed);
    // Apply mapping to raw headers and run validation immediately
    const canonicalHeaders = applyMappingToHeaders(rawHeaders, confirmed);
    const missing = validateHeaders(canonicalHeaders, fileType);
    const absent  = contract.optional.filter((c) => !canonicalHeaders.includes(c));
    setMissingCols(missing);
    setOptionalAbsent(absent);
    setStep(missing.length > 0 ? STEP.IDLE : STEP.VALIDATED);
    // If missing required columns remain after mapping, surface the error clearly
    if (missing.length > 0) {
      // Stay at IDLE so user can re-select file or re-map
    }
    fetch("/api/v1/addin/telemetry", {
      method:      "POST",
      credentials: "include",
      headers:     { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: "addin_analyze_complete",
        surface:    "webapp",
        duration_ms: null,
        metadata:   { entity_type: fileType, mapped_count: confirmed.length },
      }),
    }).catch(() => {});
  }, [rawHeaders, fileType, contract.optional]);

  const handleMappingCancel = useCallback(() => {
    setStep(STEP.IDLE);
    setFile(null);
    setRawHeaders([]);
    setMappingRows([]);
    setFetchError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleImport = useCallback(async () => {
    if (!file || step !== STEP.VALIDATED) return;

    // S1001 — quota/credit: run analyse gate first
    // For all other entity types: proceed directly to upload (unchanged path)
    if (isFeedEntity) {
      if (!feedId) {
        setFetchError("Select a Data Feed before importing.");
        return;
      }
      setStep(STEP.ANALYSING);
      setResult(null);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("source_system", SOURCE_SYSTEM);
        fd.append("file_type", fileType);
        fd.append("feed_id", feedId);
        const resp = await fetch("/api/v1/import/analyse", { method: "POST", credentials: "include", body: fd });
        let analysisJson = {};
        try { analysisJson = await resp.json(); } catch { /* empty */ }
        if (!resp.ok) {
          setResult({ status: resp.status, json: analysisJson });
          setStep(STEP.DONE);
          return;
        }
        setAnalysis(analysisJson);
        if (analysisJson.absent_rows > 0 && !analysisJson.auto_retire) {
          setStep(STEP.CONFIRMING);
          return; // wait for user decision in AbsentRowsConfirmation
        }
        // No absent rows — proceed directly to upload
        await _runUpload(analysisJson.auto_retire ? "retire" : "keep", analysisJson);
      } catch (err) {
        setResult({ status: 0, json: { error: { code: "NETWORK_ERROR", message: err.message ?? "Network error." } } });
        setStep(STEP.DONE);
      }
      return;
    }

    // Original upload path — all non-feed entity types (unchanged)
    setStep(STEP.UPLOADING);
    setResult(null);
    fetch("/api/v1/addin/telemetry", {
      method:      "POST",
      credentials: "include",
      headers:     { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: "addin_review_confirmed",
        surface:    "webapp",
        duration_ms: null,
        metadata:   { entity_type: fileType, row_count: rowCount },
      }),
    }).catch(() => {});
    const t0Import = Date.now();
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (!uploadUrl) {
        fd.append("source_system", SOURCE_SYSTEM);
        fd.append("file_type", fileType);
      }
      const resolvedUploadUrl = uploadUrl ?? "/api/v1/import/upload";
      const resp = await fetch(resolvedUploadUrl, { method: "POST", credentials: "include", body: fd });
      let json = {};
      try { json = await resp.json(); } catch { /* empty */ }
      setResult({ status: resp.status, json });
      setStep(STEP.DONE);
      if (resp.status === 200 || resp.status === 207) {
        fetch("/api/v1/addin/telemetry", {
          method:      "POST",
          credentials: "include",
          headers:     { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_type: "addin_import_completed",
            surface:    "webapp",
            duration_ms: Date.now() - t0Import,
            metadata:   {
              entity_type:   fileType,
              rows_inserted: json?.import_log?.rows_inserted ?? json?.rows_inserted ?? 0,
              rows_rejected: json?.import_log?.rows_rejected ?? json?.rows_rejected ?? 0,
            },
          }),
        }).catch(() => {});
        setHistoryKey((k) => k + 1);
        onImportSuccess?.(json);
      } else {
        fetch("/api/v1/addin/telemetry", {
          method:      "POST",
          credentials: "include",
          headers:     { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_type: "addin_import_failed",
            surface:    "webapp",
            duration_ms: Date.now() - t0Import,
            error_code:  json?.error?.code ?? null,
            metadata:   { entity_type: fileType },
          }),
        }).catch(() => {});
      }
    } catch (err) {
      fetch("/api/v1/addin/telemetry", {
        method:      "POST",
        credentials: "include",
        headers:     { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_type: "addin_import_failed",
          surface:    "webapp",
          duration_ms: Date.now() - t0Import,
          error_code:  "NETWORK_ERROR",
          metadata:   { entity_type: fileType },
        }),
      }).catch(() => {});
      setResult({ status: 0, json: { error: { code: "NETWORK_ERROR", message: err.message ?? "Network error." } } });
      setStep(STEP.DONE);
    }
  }, [file, step, fileType, rowCount, onImportSuccess, isFeedEntity, feedId, analysis]);

  // S1001 — _runUpload: called after analyse gate resolves (quota/credit only)
  // mode: 'keep' | 'retire' | 'pending'
  // analysisResult: the ImportAnalysis from /analyse
  const _runUpload = useCallback(async (mode, analysisResult) => {
    setStep(STEP.UPLOADING);
    const t0 = Date.now();
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_system", SOURCE_SYSTEM);
      fd.append("file_type", fileType);
      fd.append("feed_id", feedId);
      const resp = await fetch("/api/v1/import/upload", { method: "POST", credentials: "include", body: fd });
      let json = {};
      try { json = await resp.json(); } catch { /* empty */ }

      const logId = json?.import_log?.import_log_id ?? json?.import_log_id ?? null;
      setImportLogId(logId);

      if (!resp.ok) {
        setResult({ status: resp.status, json });
        setStep(STEP.DONE);
        return;
      }

      // Upload succeeded — now finalise based on user's decision
      if (logId && mode !== "skip") {
        setStep(STEP.FINALISING);
        try {
          const body = { import_log_id: logId, mode, feed_id: feedId };
          if (mode === "retire" && analysisResult?.analysis_token) {
            body.analysis_token = analysisResult.analysis_token;
          }
          if (mode === "pending" && analysisResult) {
            body.analysis_payload = {
              absent_rows:   analysisResult.absent_rows,
              absent_hashes: analysisResult.absent_hashes ?? [],
              absent_sample: analysisResult.absent_sample ?? [],
              analysed_at:   new Date().toISOString(),
              feed_id:       feedId,
            };
          }
          const fResp = await fetch("/api/v1/import/finalise", {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          let fJson = {};
          try { fJson = await fResp.json(); } catch { /* empty */ }
          if (mode === "pending") {
            setResult({ status: resp.status, json, finalised: { mode: "pending" } });
            setStep(STEP.PENDING);
            setHistoryKey((k) => k + 1);
            return;
          }
          setVoidedCount(fJson?.rows_voided ?? 0);
        } catch { /* finalise failure — rows committed, show result anyway */ }
      }

      fetch("/api/v1/addin/telemetry", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_type: "addin_import_completed", surface: "webapp",
          duration_ms: Date.now() - t0,
          metadata: { entity_type: fileType, rows_inserted: json?.import_log?.rows_inserted ?? 0 },
        }),
      }).catch(() => {});

      setResult({ status: resp.status, json });
      setStep(STEP.DONE);
      setHistoryKey((k) => k + 1);
      onImportSuccess?.(json);
    } catch (err) {
      setResult({ status: 0, json: { error: { code: "NETWORK_ERROR", message: err.message ?? "Network error." } } });
      setStep(STEP.DONE);
    }
  }, [file, fileType, feedId, onImportSuccess]);

  // S1001 — confirmation gate handlers (quota/credit only)
  const handleKeep = useCallback(() => { _runUpload("keep", analysis); }, [_runUpload, analysis]);
  const handleRetire = useCallback(() => { _runUpload("retire", analysis); }, [_runUpload, analysis]);
  const handleDecideLater = useCallback(() => { _runUpload("pending", analysis); }, [_runUpload, analysis]);
  const handleConfirmCancel = useCallback(() => {
    setStep(STEP.IDLE); setFile(null); setRawHeaders([]); setMappingRows([]);
    setConfirmedMap([]); setAnalysis(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleReset = useCallback(() => {
    setStep(STEP.IDLE); setFile(null); setRawHeaders([]); setMappingRows([]);
    setConfirmedMap([]); setMissingCols([]); setOptionalAbsent([]);
    setRowCount(null); setResult(null); setFetchError(null);
    setAnalysis(null); setImportLogId(null); setVoidedCount(0);
    setCreatingFeed(false);
    setPendingLog(null); setVoidedLogId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const isUploading = step === STEP.UPLOADING || step === STEP.ANALYSING || step === STEP.FINALISING;
  const validated   = [STEP.VALIDATED, STEP.UPLOADING, STEP.ANALYSING, STEP.FINALISING, STEP.CONFIRMING, STEP.DONE, STEP.PENDING].includes(step);
  const hasErrors   = missingCols.length > 0;

  // Mapping step — full replacement render
  if (step === STEP.MAPPING) {
    return (
      <div className="space-y-4">
        <FieldContractCard fileType={fileType} />
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-4">
          <ColumnMappingStep
            fileType={fileType}
            rawHeaders={rawHeaders}
            mappingRows={mappingRows}
            onConfirm={handleMappingConfirm}
            onCancel={handleMappingCancel}
          />
        </div>
      </div>
    );
  }

  // S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern.
  // Replaces entire upload UI with locked banner when period is not open.
  if (isLocked) {
    return (
      <div className="space-y-4">
        <FieldContractCard fileType={fileType} />
        <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
          <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
          <p className="text-tok-sm text-[rgb(var(--color-warning))]">
            Period is <strong>{period?.period_status ?? "not open"}</strong> — imports are not available for this period.
            Switch to an open period to import data.
          </p>
        </div>
        <ImportHistoryCard fileType={fileType} historyKey={0} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <FieldContractCard fileType={fileType} />

      <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-3">
        {/* S1001 — Feed selector: quota and credit only (isFeedEntity).
            Replaces the old Append/Replace toggle (AD-S1001-IMPORT-ANALYSE-THEN-DECIDE).
            All other entity types skip this block entirely. */}
        {isFeedEntity && !creatingFeed && (
          <div className="space-y-1.5 pb-3 border-b border-[rgb(var(--color-border))]">
            <div className="flex items-center justify-between">
              <span className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">
                Data Feed
              </span>
              <button
                type="button"
                onClick={() => setCreatingFeed(true)}
                className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline"
              >
                + New feed
              </button>
            </div>
            <select
              value={feedId ?? ""}
              onChange={(e) => setFeedId(e.target.value || null)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
            >
              <option value="" disabled>Select a feed…</option>
              {feeds.map((f) => (
                <option key={f.feed_id} value={f.feed_id}>{f.feed_name}</option>
              ))}
            </select>
            {feeds.length === 0 && (
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                No feeds yet — create one to track where this data comes from.
              </p>
            )}
            {/* S1012 OI-IMPORT-FEED-AUTO-RETIRE-UI: auto_retire_absent toggle.
                Shown only when a feed is selected. RBAC gate is in the router
                (tenant_super_admin only) — 403 is surfaced as an inline error. */}
            {feedId && (() => {
              const selectedFeed = feeds.find((f) => f.feed_id === feedId);
              if (!selectedFeed) return null;
              const isOn = selectedFeed.auto_retire_absent;
              return (
                <div className="pt-2 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                        Auto-retire absent rows
                      </span>
                      <span
                        title="When enabled, rows present in the system but absent from an uploaded file are automatically retired without a confirmation step. Requires tenant_super_admin to change."
                        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-[rgb(var(--color-border))] text-[10px] text-[rgb(var(--color-text-muted))] cursor-help"
                      >
                        ?
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleAutoRetireToggle}
                      disabled={autoRetireLoading}
                      aria-pressed={isOn}
                      className={[
                        "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
                        "focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] focus:ring-offset-1",
                        autoRetireLoading ? "opacity-50 cursor-not-allowed" : "",
                        isOn
                          ? "bg-[rgb(var(--color-primary))]"
                          : "bg-[rgb(var(--color-border))]",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200",
                          isOn ? "translate-x-4" : "translate-x-0",
                        ].join(" ")}
                      />
                    </button>
                  </div>
                  {autoRetireError && (
                    <p className="text-tok-xs text-[rgb(var(--color-danger))]">{autoRetireError}</p>
                  )}
                  {/* S1012 OI-IMPORT-FEED-ARCHIVAL: archive button.
                      Hides feed from dropdown — all history preserved. */}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                      Archive this feed
                    </span>
                    <button
                      type="button"
                      onClick={handleArchiveFeed}
                      disabled={archiveLoading}
                      className="text-tok-xs text-[rgb(var(--color-danger))] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {archiveLoading ? "Archiving…" : "Archive"}
                    </button>
                  </div>
                  {archiveError && (
                    <p className="text-tok-xs text-[rgb(var(--color-danger))]">{archiveError}</p>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        {isFeedEntity && creatingFeed && (
          <NewFeedInline
            fileType={fileType}
            onCreated={(feed) => {
              setFeeds((prev) => [...prev, feed]);
              setFeedId(feed.feed_id);
              setCreatingFeed(false);
            }}
            onCancel={() => setCreatingFeed(false)}
          />
        )}

        {/* File picker row */}
        <div className="flex items-center gap-3 flex-wrap">
          <label
            htmlFor={`file-${fileType}`}
            className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-border))] transition-colors"
          >
            Choose file
          </label>
          <input
            ref={fileInputRef}
            id={`file-${fileType}`}
            type="file"
            accept=".csv"
            className="sr-only"
            onChange={handleFileChange}
          />
          <span className="flex-1 text-tok-sm text-[rgb(var(--color-text-muted))] truncate min-w-0">
            {file ? file.name : "No file chosen"}
          </span>
          {file && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1 text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition-colors shrink-0"
            >
              <RotateCcw className="h-3 w-3" /> Clear
            </button>
          )}
        </div>

        {/* Fetching mapping spinner */}
        {step === STEP.FETCHING && (
          <div className="pt-1 border-t border-[rgb(var(--color-border))] flex items-center gap-2">
            <div className="h-3.5 w-3.5 rounded-full border-2 border-[rgb(var(--color-primary))] border-t-transparent animate-spin" />
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Loading column mapping…</p>
          </div>
        )}

        {/* Fetch error */}
        {fetchError && step === STEP.IDLE && (
          <div className="pt-1 border-t border-[rgb(var(--color-border))] space-y-1">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-[rgb(var(--color-danger))] shrink-0" />
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">Could not read file</p>
            </div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{fetchError}</p>
          </div>
        )}

        {/* Post-mapping validation errors */}
        {hasErrors && step === STEP.IDLE && missingCols.length > 0 && !fetchError && (
          <div className="pt-1 border-t border-[rgb(var(--color-border))] space-y-1.5">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-[rgb(var(--color-danger))] shrink-0" />
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">
                Required fields still missing after mapping
              </p>
            </div>
            <p className="font-mono text-tok-xs text-[rgb(var(--color-danger))] leading-relaxed">
              {missingCols.join(", ")}
            </p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Re-select the file and update your column mapping to cover all required fields.
            </p>
          </div>
        )}

        {/* Validated: row count + optional warnings + Import button */}
        {validated && !hasErrors && (
          <div className="pt-1 border-t border-[rgb(var(--color-border))] space-y-2.5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))] shrink-0" />
                <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  Columns mapped
                  {rowCount != null && (
                    <span className="font-normal text-[rgb(var(--color-text-muted))]"> — {fmt(rowCount)} data row{rowCount !== 1 ? "s" : ""}</span>
                  )}
                </p>
              </div>
              {step !== STEP.DONE && step !== STEP.PENDING && (
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={isUploading || (isFeedEntity && !feedId)}
                  title={isFeedEntity && !feedId ? "Select a Data Feed before importing" : undefined}
                  className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  {isUploading ? "Importing…" : `Import ${entityLabel}`}
                </button>
              )}
            </div>
            {optionalAbsent.length > 0 && (
              <div className="flex items-start gap-2">
                <Info className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))] shrink-0 mt-0.5" />
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Optional columns not in file: <span className="font-mono">{optionalAbsent.join(", ")}</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* S1001 — Analysing spinner */}
      {step === STEP.ANALYSING && (
        <div className="flex items-center gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
          <div className="h-3.5 w-3.5 rounded-full border-2 border-[rgb(var(--color-primary))] border-t-transparent animate-spin shrink-0" />
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Analysing file for absent records…</p>
        </div>
      )}

      {/* S1001 — Confirmation gate (absent_rows > 0) */}
      {step === STEP.CONFIRMING && analysis && (
        <AbsentRowsConfirmation
          analysis={analysis}
          onKeep={handleKeep}
          onRetire={handleRetire}
          onDecideLater={handleDecideLater}
          onCancel={handleConfirmCancel}
        />
      )}

      {/* S1011 OI-IMPORT-ANALYSE-VALIDATION-PREVIEW: show rejected row sample
          from analyse step when present. Visible in CONFIRMING state and on
          direct-to-upload path (no absent rows). RejectedRowsTable expects
          [{row_number, field, error_code, message}] — matches backend shape. */}
      {(step === STEP.CONFIRMING || step === STEP.UPLOADING || step === STEP.FINALISING) &&
        analysis?.validation_errors?.length > 0 && (
        <div className="rounded-lg border border-[rgb(var(--color-danger))] border-opacity-40 bg-[rgb(var(--color-surface))] px-4 py-3 space-y-2">
          <p className="text-tok-xs font-semibold text-[rgb(var(--color-danger))]">
            {analysis.rejected_rows} row{analysis.rejected_rows !== 1 ? "s" : ""} will be skipped
          </p>
          <RejectedRowsTable rows={analysis.validation_errors} />
        </div>
      )}

      {/* S1001 — Finalising spinner */}
      {step === STEP.FINALISING && (
        <div className="flex items-center gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
          <div className="h-3.5 w-3.5 rounded-full border-2 border-[rgb(var(--color-primary))] border-t-transparent animate-spin shrink-0" />
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Retiring absent records…</p>
        </div>
      )}

      {/* S1001 — Pending / quarantine result */}
      {step === STEP.PENDING && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 space-y-1">
          <p className="text-tok-sm font-semibold text-blue-800">Saved — decision pending</p>
          <p className="text-tok-xs text-blue-700 leading-relaxed">
            {analysis?.absent_rows?.toLocaleString() ?? "Some"} records are waiting for your decision.
            Return to Import History to resolve before this period is certified.
          </p>
        </div>
      )}

      {result && step !== STEP.PENDING && (
        <ImportResultCard result={result} voidedCount={voidedCount} isFeedEntity={isFeedEntity} />
      )}

      {/* S1003 — Pending decision banner (quota/credit only) */}
      {isFeedEntity && (
        <PendingDecisionBanner
          fileType={fileType}
          onResolve={() => {
            const historyEl = document.getElementById(`import-history-${fileType}`);
            historyEl?.scrollIntoView({ behavior: "smooth" });
          }}
        />
      )}

      <div id={`import-history-${fileType}`}>
        <ImportHistoryCard
          fileType={fileType}
          historyKey={historyKey}
          isFeedEntity={isFeedEntity}
          onResolvePending={(log) => setPendingLog(log)}
          onViewVoided={(logId) => setVoidedLogId(logId)}
        />
      </div>

      {/* S1003 — VoidedRecordsPanel slide-in drawer */}
      {voidedLogId && (
        <VoidedRecordsPanel
          importLogId={voidedLogId}
          fileType={fileType}
          onClose={() => setVoidedLogId(null)}
        />
      )}

      {/* S1003 — ReturnJourneyPanel resolve modal */}
      {pendingLog && (
        <ReturnJourneyPanel
          log={pendingLog}
          onResolved={(mode, result) => {
            setPendingLog(null);
            setHistoryKey((k) => k + 1);
            if (mode === "retire") setVoidedCount(result?.rows_voided ?? 0);
          }}
          onClose={() => setPendingLog(null)}
        />
      )}
    </div>
  );
}

// ── RT Shells — two-panel (Import + Approve) ──────────────────────────────────
//
// Approve panel:
//   GET  /api/v1/addin/lookups/pending_rate_table_approvals → { pending: [{ rate_table_id, rate_table_code }] }
//   POST /api/v1/rate-tables/{rate_table_id}/approve        (per-shell, webapp-native endpoint)
//   Error: SELF_APPROVAL_PROHIBITED

const RT_PANELS = [{ id: "import", label: "1 · Import" }, { id: "approve", label: "2 · Approve" }];

function RtShellsPanel({ isLocked, period }) {
  const [activePanel,     setActivePanel]     = useState("import");
  const [pendingList,     setPendingList]     = useState([]);
  const [pendingLoading,  setPendingLoading]  = useState(false);
  const [pendingError,    setPendingError]    = useState(null);
  const [selectedIds,     setSelectedIds]     = useState([]);
  const [approving,       setApproving]       = useState(false);
  const [approvalResult,  setApprovalResult]  = useState(null);
  const [approvalError,   setApprovalError]   = useState(null);
  const [justImported,    setJustImported]    = useState(false);

  const loadPending = useCallback(async () => {
    setPendingLoading(true); setPendingError(null);
    try {
      const r = await fetch("/api/v1/addin/lookups/pending_rate_table_approvals", { credentials: "include" });
      const d = await r.json();
      // S568: endpoint returns `items` not `pending` — align with actual response shape
      setPendingList(d.items ?? d.pending ?? []);
    } catch { setPendingError("Could not load pending shells."); }
    finally { setPendingLoading(false); }
  }, []);

  useEffect(() => { if (activePanel === "approve") loadPending(); }, [activePanel, loadPending]);

  // S568: Auto-select all shells on load so CA-2 sees all checked by default.
  // Deselect exceptions before approving. Fixes "Approve 1 shell" label bug
  // caused by selectedIds being empty when pendingList first populates.
  useEffect(() => {
    if (pendingList.length > 0) {
      setSelectedIds(pendingList.map((s) => s.rate_table_id));
    }
  }, [pendingList]);

  const toggleShell = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((s) => s !== id) : [...p, id]);
  const toggleAll   = () => setSelectedIds(selectedIds.length === pendingList.length ? [] : pendingList.map((s) => s.rate_table_id));

  const handleApprove = useCallback(async () => {
    if (!selectedIds.length) return;
    setApproving(true); setApprovalError(null); setApprovalResult(null);
    let approvedCount = 0; let lastError = null;
    for (const id of selectedIds) {
      try {
        const r = await fetch(`/api/v1/rate-tables/${id}/approve`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } });
        const d = await r.json();
        if (r.ok) { approvedCount++; }
        else {
          const code = d?.detail?.error?.code ?? d?.error?.code ?? d?.error;
          lastError = friendlyApprovalError(code, d?.detail?.error?.message ?? d?.error?.message);
        }
      } catch (err) { lastError = err.message ?? "Approval failed."; }
    }
    setApproving(false);
    if (approvedCount > 0) {
      setApprovalResult({ approved_count: approvedCount });
      setSelectedIds([]);
      loadPending();
    }
    if (lastError) setApprovalError(lastError);
  }, [selectedIds, loadPending]);

  const handleImportSuccess = useCallback(() => {
    setJustImported(true);
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Rate Table Shells</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">Import shells, then approve before loading rate bands.</p>
      </div>
      <PanelSwitcher panels={RT_PANELS} activePanel={activePanel} onPanelChange={(p) => { setActivePanel(p); setApprovalResult(null); setApprovalError(null); }} />

      {activePanel === "import" && (
        <>
          <EntityUploadPanel fileType="rate_table_shell" entityLabel="RT Shells" onImportSuccess={handleImportSuccess} isLocked={isLocked} period={period} />
          {justImported && (
            <button
              type="button"
              onClick={() => { setJustImported(false); setActivePanel("approve"); }}
              className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 transition-opacity"
            >
              Approve shells →
            </button>
          )}
        </>
      )}

      {activePanel === "approve" && (
        <div className="space-y-4">
          {approvalResult && (
            <div className="rounded-lg border border-[rgb(var(--color-success))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))] shrink-0" />
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                  {approvalResult.approved_count} shell{approvalResult.approved_count !== 1 ? "s" : ""} approved — rate bands can now be loaded
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Pending shells</p>
            <button type="button" onClick={loadPending} disabled={pendingLoading} className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline disabled:opacity-50">
              {pendingLoading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {pendingError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{pendingError}</p>}
          {!pendingLoading && !pendingError && !pendingList.length && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">No pending shells awaiting approval.</p>
          )}

          {pendingList.length > 0 && (
            <div className="space-y-2">
              <div className="rounded-lg border border-[rgb(var(--color-border))] overflow-hidden max-h-48 overflow-y-auto">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="w-full text-left px-3 py-2 text-tok-xs text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]"
                >
                  {selectedIds.length === pendingList.length ? "Deselect all" : "Select all"}
                </button>
                {pendingList.map((shell) => (
                  // S568: Use div+onClick instead of label to prevent double-fire.
                  // label wrapping an input fires onChange twice on row click.
                  <div key={shell.rate_table_id} onClick={() => toggleShell(shell.rate_table_id)} className="flex items-center gap-2.5 px-3 py-2 hover:bg-[rgb(var(--color-surface-muted))] cursor-pointer border-b border-[rgb(var(--color-border))] last:border-0">
                    <input type="checkbox" checked={selectedIds.includes(shell.rate_table_id)} onChange={() => {}} className="shrink-0 pointer-events-none" />
                    <span className="font-mono text-tok-xs text-[rgb(var(--color-text))]">{shell.rate_table_code}</span>
                  </div>
                ))}
              </div>

              {approvalError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{approvalError}</p>}

              <button
                type="button"
                onClick={handleApprove}
                disabled={!selectedIds.length || approving}
                className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                {approving ? "Approving…" : `Approve ${selectedIds.length > 0 ? selectedIds.length + " " : ""}shell${selectedIds.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Plans — two-panel (Upload + Approve) ─────────────────────────────────────
//
// Upload panel: after success, plan_import_session_id displayed for Actor 1 to share.
// Approve panel:
//   GET  /api/v1/addin/lookups/pending_plan_approvals → { pending: [{ plan_import_session_id, plan_count, first_plan_code }] }
//   POST /api/v1/plans/import/approve                 → { plans_approved, approved_at, approved_by }
//   Errors: SELF_APPROVAL_PROHIBITED | PLAN_ALREADY_APPROVED | PLAN_SESSION_NOT_FOUND

const PLAN_PANELS = [{ id: "upload", label: "1 · Upload" }, { id: "approve", label: "2 · Approve" }];

function PlansPanel({ isLocked, period }) {
  const [activePanel,      setActivePanel]      = useState("upload");
  const [sessionId,        setSessionId]        = useState(null);
  const [pendingList,      setPendingList]      = useState([]);
  const [pendingLoading,   setPendingLoading]   = useState(false);
  const [pendingError,     setPendingError]     = useState(null);
  const [sessionIdInput,   setSessionIdInput]   = useState("");
  const [selectedSession,  setSelectedSession]  = useState(null);
  const [approving,        setApproving]        = useState(false);
  const [approvalResult,   setApprovalResult]   = useState(null);
  const [approvalError,    setApprovalError]    = useState(null);

  const loadPending = useCallback(async () => {
    setPendingLoading(true); setPendingError(null);
    try {
      const r = await fetch("/api/v1/addin/lookups/pending_plan_approvals", { credentials: "include" });
      const d = await r.json();
      // S568: endpoint returns `items` not `pending` — align with actual response shape
      setPendingList(d.items ?? d.pending ?? []);
    } catch { setPendingError("Could not load pending plan batches."); }
    finally { setPendingLoading(false); }
  }, []);

  useEffect(() => { if (activePanel === "approve") loadPending(); }, [activePanel, loadPending]);

  const handleImportSuccess = useCallback((json) => {
    const sid = json?.import_log?.plan_import_session_id ?? json?.plan_import_session_id ?? null;
    setSessionId(sid);
  }, []);

  const handleSelectSession = (sid, planCount, firstPlanCode) => {
    setSelectedSession({ sessionId: sid, planCount, firstPlanCode });
    setSessionIdInput(sid);
    setApprovalError(null);
  };

  const handleSessionIdSubmit = () => {
    const t = sessionIdInput.trim();
    if (!t) return;
    const match = pendingList.find((p) => p.plan_import_session_id === t);
    setSelectedSession({ sessionId: t, planCount: match?.plan_count ?? null, firstPlanCode: match?.first_plan_code ?? null });
    setApprovalError(null);
  };

  const handleApprove = useCallback(async () => {
    if (!selectedSession?.sessionId) return;
    setApproving(true); setApprovalError(null); setApprovalResult(null);
    try {
      const r = await fetch("/api/v1/plans/import/approve", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_import_session_id: selectedSession.sessionId }),
      });
      const d = await r.json();
      if (r.ok) {
        setApprovalResult(d);
        setSelectedSession(null);
        setSessionIdInput("");
        loadPending();
      } else {
        const code = d?.detail?.error?.code ?? d?.error?.code ?? d?.error;
        setApprovalError(friendlyApprovalError(code, d?.detail?.error?.message ?? d?.error?.message));
      }
    } catch (err) { setApprovalError(err.message ?? "Approval failed."); }
    finally { setApproving(false); }
  }, [selectedSession, loadPending]);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Plans</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">Actor 1 imports plans in draft · Actor 2 approves.</p>
      </div>
      <PanelSwitcher panels={PLAN_PANELS} activePanel={activePanel} onPanelChange={(p) => { setActivePanel(p); setApprovalResult(null); setApprovalError(null); }} />

      {activePanel === "upload" && (
        <>
          <EntityUploadPanel fileType="plan" entityLabel="Plans" onImportSuccess={handleImportSuccess} isLocked={isLocked} period={period} />
          {sessionId && (
            <div className="rounded-lg border border-[rgb(var(--color-primary))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-2">
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Plans created in draft — awaiting approval</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Share this session ID with the approving comp admin:</p>
              <p className="font-mono text-tok-xs text-[rgb(var(--color-primary))] break-all select-all">{sessionId}</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">The approver opens the <strong>2 · Approve</strong> tab and enters this ID.</p>
              <button
                type="button"
                onClick={() => setActivePanel("approve")}
                className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 transition-opacity"
              >
                Approve plans →
              </button>
            </div>
          )}
        </>
      )}

      {activePanel === "approve" && (
        <div className="space-y-4">
          {approvalResult && (
            <div className="rounded-lg border border-[rgb(var(--color-success))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))] shrink-0" />
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                  {approvalResult.plans_approved ?? 0} plan{(approvalResult.plans_approved ?? 0) !== 1 ? "s" : ""} approved — plan assignments can now be loaded
                </p>
              </div>
              {approvalResult.approved_at && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Approved at {new Date(approvalResult.approved_at).toLocaleString()}</p>}
              {approvalResult.approved_by && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Approved by <span className="font-mono">{approvalResult.approved_by}</span></p>}
              <button type="button" onClick={() => { setApprovalResult(null); loadPending(); }} className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline">
                Approve another batch
              </button>
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Pending approvals</p>
            <button type="button" onClick={loadPending} disabled={pendingLoading} className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline disabled:opacity-50">
              {pendingLoading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {pendingError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{pendingError}</p>}
          {!pendingLoading && !pendingError && !pendingList.length && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">No pending plan batches.</p>
          )}

          {pendingList.length > 0 && (
            <div className="rounded-lg border border-[rgb(var(--color-border))] overflow-hidden max-h-36 overflow-y-auto">
              {pendingList.map((item) => {
                const isSel = selectedSession?.sessionId === item.plan_import_session_id;
                return (
                  <button
                    key={item.plan_import_session_id}
                    type="button"
                    onClick={() => handleSelectSession(item.plan_import_session_id, item.plan_count, item.first_plan_code)}
                    className={[
                      "w-full flex flex-col px-3 py-2 text-left transition-colors border-b border-[rgb(var(--color-border))] last:border-0",
                      isSel ? "bg-[rgb(var(--color-primary))] bg-opacity-10 border-l-2 border-l-[rgb(var(--color-primary))]" : "hover:bg-[rgb(var(--color-surface-muted))]",
                    ].join(" ")}
                  >
                    <span className="font-mono text-tok-xs text-[rgb(var(--color-text))] truncate">{item.plan_import_session_id}</span>
                    <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{item.plan_count} plan{item.plan_count !== 1 ? "s" : ""} · first: {item.first_plan_code}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-tok-xs font-medium text-[rgb(var(--color-text))]">Or enter session ID manually</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={sessionIdInput}
                onChange={(e) => setSessionIdInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSessionIdSubmit()}
                placeholder="plan_import_session_id (UUID)"
                className="flex-1 font-mono text-tok-xs rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
              />
              <button
                type="button"
                onClick={handleSessionIdSubmit}
                disabled={!sessionIdInput.trim()}
                className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-border))] disabled:opacity-50 transition-colors"
              >
                Load
              </button>
            </div>
          </div>

          {selectedSession && (
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-1">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Batch to approve</p>
              <p className="font-mono text-tok-xs text-[rgb(var(--color-text))] break-all">{selectedSession.sessionId}</p>
              {selectedSession.planCount != null && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{selectedSession.planCount} plan{selectedSession.planCount !== 1 ? "s" : ""}</p>}
              {selectedSession.firstPlanCode && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">First plan: <span className="font-mono text-[rgb(var(--color-text))]">{selectedSession.firstPlanCode}</span></p>}
            </div>
          )}

          {approvalError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{approvalError}</p>}

          {selectedSession && (
            <button
              type="button"
              onClick={handleApprove}
              disabled={approving}
              className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {approving ? "Approving…" : "Approve all plans"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Ramp Schedules — upload panel with mixed-effective-date warning surface ───
//
// Wraps EntityUploadPanel. On a successful 200 response, checks result.error_summary
// and surfaces it as a non-blocking amber warning banner (data was written).
// error_summary is populated by §87.8 when the same role_code appears with more
// than one distinct effective_date in the batch (S557 D1).

function RampSchedulePanel({ isLocked, period }) {
  const [warningMessage, setWarningMessage] = useState(null);

  const handleImportSuccess = useCallback((json) => {
    setWarningMessage(json?.error_summary ?? null);
  }, []);

  return (
    <div className="space-y-4">
      <EntityUploadPanel
        fileType="ramp_schedule"
        entityLabel="Ramp Schedules"
        onImportSuccess={handleImportSuccess}
        isLocked={isLocked}
        period={period}
      />
      {warningMessage && (
        <div className="rounded-lg border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-[rgb(var(--color-warning))] shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">Import warning — data was written</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">{warningMessage}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── OiStubPanel — greyed placeholder for future import surfaces ──────────────
// Rendered when user clicks an oi:true tab. Surfaces the OI code and workaround.

function OiStubPanel({ label, oiCode, description }) {
  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-5 py-6 space-y-3">
      <div className="flex items-center gap-2">
        <span className="inline-block h-2 w-2 rounded-full bg-[rgb(var(--color-warning))]" />
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text-muted))]">{label} — coming soon</p>
      </div>
      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed max-w-xl">{description}</p>
      <p className="font-mono text-[10px] text-[rgb(var(--color-text-muted))] opacity-60">{oiCode}</p>
    </div>
  );
}

// ── FX Rates tab ──────────────────────────────────────────────────────────────
// C13 S955 — FX rate CSV import surface + FXRateManager approval remount.
// Upload: EntityUploadPanel with uploadUrl override → POST /api/v1/exchange-rates/import?fiscal_period_id=<id>
// Approval: FXRateManager remounted inline — CA-Two approves pending rows here.
// Feature-gated: feature.tier.fx_rate_management (same gate as AdminFlowShell was using).

function FXRatesTab({ period, isLocked }) {
  const { hasFeature } = useFeatures();
  // S956: allow_normalization — CA-One explicit flag for reverse-orientation CSV rows.
  // FX-CSV-BIDIRECTIONAL-GOVERNANCE-RULE: silent inversion prohibited.
  // NOTE: declared before conditional return (Rules of Hooks).
  const [allowNormalization, setAllowNormalization] = useState(false);

  if (!hasFeature("feature.tier.fx_rate_management")) {
    return (
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-5 py-6">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[rgb(var(--color-text-muted))] shrink-0" />
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text-muted))]">FX Rate Management</p>
        </div>
        <p className="mt-2 text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
          FX rate management is not enabled for your subscription tier. Contact support to enable multi-currency compensation features.
        </p>
      </div>
    );
  }

  // Upload URL includes fiscal_period_id so the endpoint can scope the import correctly.
  // fiscal_period_id is required by POST /api/v1/exchange-rates/import (fx_router.py C10).
  const fiscalPeriodId = period?.fiscal_period_id ?? null;
  const fxUploadUrl = fiscalPeriodId
    ? `/api/v1/exchange-rates/import?fiscal_period_id=${fiscalPeriodId}${allowNormalization ? '&allow_normalization=true' : ''}`
    : `/api/v1/exchange-rates/import${allowNormalization ? '?allow_normalization=true' : ''}`;

  return (
    <div className="space-y-6">

      {/* S956: CA-One normalization flag — must be set before upload */}
      <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 flex flex-col gap-2">
        <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">CSV Orientation</p>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={allowNormalization}
            onChange={(e) => setAllowNormalization(e.target.checked)}
            className="mt-0.5 rounded"
          />
          <span className="text-tok-xs text-[rgb(var(--color-text))] leading-relaxed">
            <strong>Authorise orientation normalisation</strong> — my CSV file contains rates in reverse orientation
            (e.g. base→local instead of local→base). I authorise the system to invert these rows to canonical
            local→base form on import. This decision will be recorded on the rate set and visible to the approver.
          </span>
        </label>
        {allowNormalization && (
          <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/20 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
            ⚠ Normalisation authorised. The stored rates will differ from the values in your source file.
            The approver will see this flag and the original source orientation is permanently recorded.
          </div>
        )}
      </div>

      {/* Upload surface */}
      <EntityUploadPanel
        fileType="exchange_rate"
        entityLabel="FX Rates"
        isLocked={isLocked}
        period={period}
        uploadUrl={fxUploadUrl}
      />

      {/* FXRateManager remounted — rate set approval + manual row management */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">FX Rate Approval</p>
        </div>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
          Imported rows are grouped into a governed rate set — Pending Approval.
          Approve the rate set header in one event to make all rates engine-eligible.
          Manual rates (no rate set) use row-level approval below.
        </p>
        {fiscalPeriodId ? (
          <FXRateManager fiscalPeriodId={fiscalPeriodId} />
        ) : (
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">No active period — select a period above to manage FX rates.</p>
        )}
      </div>
    </div>
  );
}

// ── NewFeedInline — S1001 ────────────────────────────────────────────────────
// Inline feed creation inside EntityUploadPanel (quota/credit tabs).
// Spec: DecisionFlow_Import_UX_Spec_v1.md §5.3

function NewFeedInline({ fileType, onCreated, onCancel }) {
  const [feedName, setFeedName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error,    setError]    = useState(null);

  const handleCreate = useCallback(async () => {
    const name = feedName.trim();
    if (!name) { setError("Feed name is required."); return; }
    if (name.length > 200) { setError("Feed name must be 200 characters or fewer."); return; }
    setCreating(true); setError(null);
    try {
      const resp = await fetch("/api/v1/import/feeds", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feed_name: name, file_type: fileType, source_system: SOURCE_SYSTEM }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data?.error?.message ?? data?.detail ?? "Failed to create feed.");
        return;
      }
      onCreated(data);
    } catch (err) {
      setError(err.message ?? "Network error.");
    } finally {
      setCreating(false);
    }
  }, [feedName, fileType, onCreated]);

  return (
    <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-2 pb-3 border-b border-[rgb(var(--color-border))]">
      <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">New Data Feed</p>
      <input
        type="text"
        value={feedName}
        onChange={(e) => setFeedName(e.target.value)}
        placeholder={`e.g. ${fileType === "credit" ? "CRM IC Credits" : "HRIS Quota Roster"}`}
        maxLength={200}
        autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
        className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
      />
      {error && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={creating}
          className="flex-1 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-border))] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating || !feedName.trim()}
          className="flex-1 rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {creating ? "Creating…" : "Create"}
        </button>
      </div>
    </div>
  );
}

// ── AbsentRowsConfirmation — S1001 ───────────────────────────────────────────
// Confirmation gate rendered when analysis.absent_rows > 0.
// Three options: Keep / Retire / Decide Later.
// Spec: DecisionFlow_Import_UX_Spec_v1.md §5.5

function AbsentRowsConfirmation({ analysis, onKeep, onRetire, onDecideLater, onCancel }) {
  const [selected, setSelected] = useState(null); // 'keep' | 'retire'
  const sample        = (analysis.absent_sample ?? []).slice(0, 3);
  const remainingMore = analysis.absent_rows - sample.length;

  const handleConfirm = useCallback(() => {
    if (selected === "keep") onKeep();
    else if (selected === "retire") onRetire();
  }, [selected, onKeep, onRetire]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="rounded-lg border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-1">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-[rgb(var(--color-warning))] shrink-0" />
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">
            {fmt(analysis.absent_rows)} record{analysis.absent_rows !== 1 ? "s" : ""} in the system but not in this file
          </p>
        </div>
        {sample.length > 0 && (
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] pl-6">
            Includes: {sample.map((r) => r.employee_id || r.position_id).filter(Boolean).join(", ")}
            {remainingMore > 0 ? ` +${fmt(remainingMore)} more` : ""}
          </p>
        )}
      </div>

      {/* Options */}
      <div className="space-y-2">
        {/* Keep */}
        <label
          className={[
            "flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors",
            selected === "keep"
              ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-surface-muted))]"
              : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] hover:border-[rgb(var(--color-primary))]",
          ].join(" ")}
        >
          <input
            type="radio"
            className="mt-0.5 shrink-0"
            checked={selected === "keep"}
            onChange={() => setSelected("keep")}
          />
          <div className="space-y-0.5">
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Keep them active</p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
              Prior records stay. Only this file's new and changed records are updated.
            </p>
          </div>
        </label>

        {/* Retire */}
        <label
          className={[
            "flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors",
            selected === "retire"
              ? "border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface-muted))]"
              : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] hover:border-[rgb(var(--color-warning))]",
          ].join(" ")}
        >
          <input
            type="radio"
            className="mt-0.5 shrink-0"
            checked={selected === "retire"}
            onChange={() => setSelected("retire")}
          />
          <div className="space-y-0.5">
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Retire them</p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
              This file is the complete dataset. Records not in this file will be retired and excluded from calculations.
            </p>
          </div>
        </label>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!selected}
          className="w-full rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          Confirm
        </button>
        <button
          type="button"
          onClick={onDecideLater}
          className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-border))] transition-colors"
          title={`Save your file's changes now. Return to Import History to resolve the ${fmt(analysis.absent_rows)} records before this period is certified.`}
        >
          Decide Later
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="w-full text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition-colors py-1"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Import Log tab ────────────────────────────────────────────────────────────

function ImportLogTab() {
  return <ImportLogViewer aiStatus="idle" aiResponse={null} currentScreen="ImportLogViewer" onNavigate={() => {}} />;
}

// ── ImportDataPanel root ──────────────────────────────────────────────────────

export { EntityUploadPanel };

// Valid tab IDs for deep-link guard
const VALID_TAB_IDS = new Set(TABS.flatMap((t) => t.subTabs ? t.subTabs.map((s) => s.id) : [t.id]));

export default function ImportDataPanel({ period, initialTab }) {
  // S914: activeTab is derived from the URL ?tab= param via useSearchParams so that
  // clicking Data Load sub-nav items (which change only the query string) instantly
  // switches tabs without remounting the component. initialTab prop is the fallback
  // when no ?tab= param is present (e.g. direct programmatic render without a URL).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const activeTab = (urlTab && VALID_TAB_IDS.has(urlTab))
    ? urlTab
    : (initialTab && VALID_TAB_IDS.has(initialTab))
    ? initialTab
    : "positions";

  // Tab change: update URL param — keeps browser history and back-button working.
  const setActiveTab = useCallback((tabId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tabId);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [activeRateTableSubTab, setActiveRateTableSubTab] = useState("rate_table_shell");
  const [activePlansSubTab,     setActivePlansSubTab]     = useState("plan");
  const [guidedTooltip,         setGuidedTooltip]         = useState(false);

  // S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly.
  // Gates all file upload UI when period is not open (locked or closed).
  const isLocked = period?.period_status !== "open";

  return (
    <div className="space-y-0">
      {/* Panel header */}
      <div className="flex items-start justify-between pb-4">
        <div>
          <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Import Data</h2>
          {period && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
              Active period:{" "}
              <span className="font-medium text-[rgb(var(--color-text))]">{period.period_label ?? period.period_code}</span>
              {" "}· SETUP imports are tenant-level — delta imports run inside each EXECUTE panel
            </p>
          )}
        </div>
        {/* Guide me stub — OI-WEBAPP-AGENT-IMPORT */}
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setGuidedTooltip((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
          >
            <Sparkles className="h-3.5 w-3.5" /> Guide me +
          </button>
          {guidedTooltip && (
            <div className="absolute right-0 top-full mt-2 z-20 w-64 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-3 shadow-lg space-y-1.5">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">AI-guided import</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
                Agent-assisted import is coming to the webapp. Use the Excel Add-in today for guided import with column mapping and pre-flight analysis.
              </p>
              <p className="font-mono text-[10px] text-[rgb(var(--color-text-muted))]">OI-WEBAPP-AGENT-IMPORT</p>
              <button type="button" onClick={() => setGuidedTooltip(false)} className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition-colors">Dismiss</button>
            </div>
          )}
        </div>
      </div>

      <TabBar tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="pt-5">
        {activeTab === "positions" && (
          <EntityUploadPanel fileType="position" entityLabel="Positions" isLocked={isLocked} period={period} />
        )}
        {activeTab === "ote_history" && (
          <EntityUploadPanel fileType="ote_history" entityLabel="OTE History" isLocked={isLocked} period={period} />
        )}
        {activeTab === "rate_tables" && (
          <>
            <SubTabBar subTabs={TABS.find((t) => t.id === "rate_tables").subTabs} activeSubTab={activeRateTableSubTab} onSubTabChange={setActiveRateTableSubTab} />
            {activeRateTableSubTab === "rate_table_shell" && <RtShellsPanel isLocked={isLocked} period={period} />}
            {activeRateTableSubTab === "rate_table_row"   && <EntityUploadPanel fileType="rate_table_row" entityLabel="Rate Table Rows" isLocked={isLocked} period={period} />}
          </>
        )}
        {activeTab === "ramp_schedules" && (
          <RampSchedulePanel isLocked={isLocked} period={period} />
        )}
        {activeTab === "plans_group" && (
          <>
            <SubTabBar subTabs={TABS.find((t) => t.id === "plans_group").subTabs} activeSubTab={activePlansSubTab} onSubTabChange={setActivePlansSubTab} />
            {activePlansSubTab === "plan"            && <PlansPanel isLocked={isLocked} period={period} />}
            {activePlansSubTab === "plan_assignment" && <EntityUploadPanel fileType="plan_assignment" entityLabel="Plan Assignments" isLocked={isLocked} period={period} />}
          </>
        )}
        {activeTab === "quotas" && (
          <EntityUploadPanel fileType="quota" entityLabel="Quotas" isLocked={isLocked} period={period} />
        )}
        {activeTab === "credits" && (
          <EntityUploadPanel fileType="credit" entityLabel="Credits" isLocked={isLocked} period={period} />
        )}
        {activeTab === "mbo_result" && (
          // S914 G-02 — MBO results upload. EntityUploadPanel handles column mapping,
          // dedup, partial load, hash, import history consistently with all other tabs.
          // AD-106: mbo_router CRUD not used — POST /api/v1/import/upload file_type=mbo_result.
          <EntityUploadPanel fileType="mbo_result" entityLabel="MBO Results" isLocked={isLocked} period={period} />
        )}
        {activeTab === "nps_result" && (
          // S930 G-17 — NPS result upload. Same EntityUploadPanel pattern as MBO Results.
          // import_service.py: _validate_nps_result_rows + _insert_nps_result_rows.
          // nps_score validated in [-100, 100]. Upsert: (tenant_id, position_id, fiscal_period_id).
          // AD-S929-01: nps_router POST endpoints not used — POST /api/v1/import/upload file_type=nps_result.
          <EntityUploadPanel fileType="nps_result" entityLabel="NPS Results" isLocked={isLocked} period={period} />
        )}
        {activeTab === "adoption_result" && (
          // S930 G-18 — Adoption result upload. Same EntityUploadPanel pattern as MBO Results.
          // import_service.py: _validate_adoption_result_rows + _insert_adoption_result_rows.
          // milestone_code resolved server-side → adoption_milestone_id.
          // achievement_value required (NN in DDL). achieved_flag optional (nullable).
          // AD-S929-01: adoption_router POST endpoints not used — POST /api/v1/import/upload file_type=adoption_result.
          <EntityUploadPanel fileType="adoption_result" entityLabel="Adoption Results" isLocked={isLocked} period={period} />
        )}
        {/* ── OI stubs — greyed tab indicator shown in TabBar via oi:true flag ── */}
        {activeTab === "churn_events" && (
          <OiStubPanel
            label="Churn Events"
            oiCode="OI-S910-TIER2-IMPORT-UI-SURFACE-MISSING"
            description="Churn event import surface is not yet built. Backend pipeline required (G-13). Use the API directly: POST /api/v1/import/upload file_type=churn_event."
          />
        )}
        {activeTab === "spiff_awards" && (
          <OiStubPanel
            label="SPIFF Awards"
            oiCode="OI-S910-TIER2-IMPORT-UI-SURFACE-MISSING"
            description="SPIFF award import surface is not yet built. Backend pipeline required (G-12). Use the API directly: POST /api/v1/import/upload file_type=spiff_award."
          />
        )}
        {activeTab === "myd_schedule" && (
          <OiStubPanel
            label="MYD Schedule"
            oiCode="OI-S910-TIER2-IMPORT-UI-SURFACE-MISSING"
            description="Multi-year deal schedule import surface is not yet built. Backend pipeline required (G-15). Use the API directly: POST /api/v1/import/upload file_type=myd_schedule."
          />
        )}
        {activeTab === "aop_inputs" && (
          <EntityUploadPanel fileType="aop_input" entityLabel="AOP Inputs" isLocked={isLocked} period={period} />
        )}
        {/* C13 S955 — FX Rates tab: CSV import → approval. Feature-gated inside FXRatesTab. */}
        {activeTab === "fx_rates" && (
          <FXRatesTab period={period} isLocked={isLocked} />
        )}
        {activeTab === "import_log" && <ImportLogTab />}
      </div>
    </div>
  );
}
